<template>
  <div class="ai-chat-panel">
    <!-- Header -->
    <div class="chat-header">
      <div class="header-left">
        <div class="header-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5"/>
            <path d="M12 2C12 2 14.5 5.5 14.5 12C14.5 18.5 12 22 12 22" stroke="currentColor" stroke-width="1.5"/>
            <path d="M12 2C12 2 9.5 5.5 9.5 12C9.5 18.5 12 22 12 22" stroke="currentColor" stroke-width="1.5"/>
            <path d="M2 12H22" stroke="currentColor" stroke-width="1.5"/>
          </svg>
        </div>
        <span class="header-title">{{ title }}</span>
      </div>
      <div class="header-actions">
        <button
          type="button"
          class="icon-button toolkit-button"
          @click="showToolkitModal = true"
          title="Open Toolkit"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <button
          type="button"
          class="icon-button"
          @click="resetChat"
          :disabled="messages.length === 0"
          title="New chat"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 4V20M4 12H20" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Messages area -->
    <div class="messages-container" ref="messagesContainer">
      <div v-if="messages.length === 0" class="empty-state">
        <div class="empty-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5"/>
            <path d="M12 2C12 2 14.5 5.5 14.5 12C14.5 18.5 12 22 12 22" stroke="currentColor" stroke-width="1.5"/>
            <path d="M12 2C12 2 9.5 5.5 9.5 12C9.5 18.5 12 22 12 22" stroke="currentColor" stroke-width="1.5"/>
            <path d="M2 12H22" stroke="currentColor" stroke-width="1.5"/>
            <path d="M4 7H20" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            <path d="M4 17H20" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </div>
        <h3 class="empty-title">How can I help you today?</h3>
        <p class="empty-subtitle">Ask me about campaigns, impressions, or performance data.</p>
      </div>

      <div v-else class="messages-list">
        <div
          v-for="(message, index) in messages"
          :key="index"
          class="message"
          :class="message.role"
        >
          <div class="message-avatar" v-if="message.role === 'assistant'">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5"/>
              <path d="M12 2C12 2 14.5 5.5 14.5 12C14.5 18.5 12 22 12 22" stroke="currentColor" stroke-width="1.5"/>
              <path d="M2 12H22" stroke="currentColor" stroke-width="1.5"/>
            </svg>
          </div>
          <div class="message-avatar user-avatar" v-else>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="8" r="4" stroke="currentColor" stroke-width="2"/>
              <path d="M4 20C4 16.6863 7.58172 14 12 14C16.4183 14 20 16.6863 20 20" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </div>
          <div class="message-body">
            <div class="message-header-row">
              <div class="message-role">{{ message.role === 'assistant' ? 'Atlas' : 'You' }}</div>
              <!-- Copy button for assistant messages -->
              <button
                v-if="message.role === 'assistant' && !message.streaming"
                type="button"
                class="copy-button"
                @click="copyToClipboard(message, index)"
                :title="message.copied ? 'Copied!' : 'Copy to clipboard'"
              >
                <svg v-if="!message.copied" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" stroke-width="2"/>
                  <path d="M5 15H4C2.89543 15 2 14.1046 2 13V4C2 2.89543 2.89543 2 4 2H13C14.1046 2 15 2.89543 15 4V5" stroke="currentColor" stroke-width="2"/>
                </svg>
                <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>
            <!-- Recipe message display -->
            <div v-if="message.isRecipe" class="recipe-message">
              <div class="recipe-badge">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span>Recipe</span>
              </div>
              <div class="recipe-name">{{ message.recipeName }}</div>
              <div class="recipe-params">{{ message.recipeParamDisplay }}</div>
            </div>
            <!-- Normal message display -->
            <div
              v-else
              class="message-content"
              :class="{
                'markdown-content': message.role === 'assistant',
                'loading-phrase': message.isLoadingPhrase,
                'phrase-fading': message.phraseFading
              }"
              v-html="message.role === 'assistant' ? (message.streaming || message.isLoadingPhrase ? message.content : parseMarkdown(message.content)) : message.content"
            ></div>

            <!-- Interpretation Status Banner -->
            <div
              v-if="message.role === 'assistant' && message.interpretation && !message.streaming"
              class="interpretation-banner"
              :class="[
                `severity-${message.interpretation.severity}`,
                { 'action-required': message.interpretation.actionRequired }
              ]"
            >
              <div class="banner-icon">
                <svg v-if="message.interpretation.severity === 'info'" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                  <path d="M12 16V12M12 8H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
                <svg v-else-if="message.interpretation.severity === 'warning'" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 9V13M12 17H12.01M10.29 3.86L1.82 18C1.64 18.3 1.55 18.64 1.55 19C1.55 19.36 1.64 19.7 1.82 20C2 20.3 2.26 20.56 2.56 20.74C2.86 20.92 3.21 21.01 3.56 21.01H20.44C20.79 21.01 21.14 20.92 21.44 20.74C21.74 20.56 22 20.3 22.18 20C22.36 19.7 22.45 19.36 22.45 19C22.45 18.64 22.36 18.3 22.18 18L13.71 3.86C13.53 3.56 13.27 3.32 12.97 3.14C12.67 2.96 12.34 2.87 12 2.87C11.66 2.87 11.33 2.96 11.03 3.14C10.73 3.32 10.47 3.56 10.29 3.86Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                  <path d="M15 9L9 15M9 9L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </div>
              <div class="banner-content">
                <div class="banner-summary">
                  {{ message.interpretation.summary }}
                  <span v-if="message.interpretation.actionRequired" class="action-badge">Action Required</span>
                </div>
                <ul v-if="message.interpretation.details && message.interpretation.details.length > 0" class="banner-details">
                  <li v-for="(detail, dIndex) in message.interpretation.details" :key="dIndex">{{ detail }}</li>
                </ul>
              </div>
            </div>

            <!-- Collapsible Tool Results -->
            <div v-if="message.role === 'assistant' && (message.table || message.chart) && !message.streaming" class="tool-results-container">
              <button
                type="button"
                class="tool-results-header"
                @click="toggleToolResults(index)"
              >
                <div class="tool-results-info">
                  <div class="tool-results-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>
                  <div class="tool-results-text">
                    <span class="tool-results-title">
                      {{ message.table ? (message.table.title || 'Data Results') : 'Chart Visualization' }}
                    </span>
                    <span class="tool-results-meta">
                      <span v-if="message.table">{{ message.table.rows.length }} rows</span>
                      <span v-if="message.table && message.chart"> &bull; </span>
                      <span v-if="message.chart">{{ message.chart.options.chart.type }} chart</span>
                    </span>
                  </div>
                </div>
                <div class="tool-results-toggle">
                  <span class="toggle-label">{{ isToolResultsExpanded(index) ? 'Hide' : 'Show' }}</span>
                  <svg
                    class="toggle-chevron"
                    :class="{ 'expanded': isToolResultsExpanded(index) }"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </button>

              <div class="tool-results-content" :class="{ 'expanded': isToolResultsExpanded(index) }">
                <!-- Interactive Data Table -->
                <div v-if="message.table" class="message-table-container">
                  <div class="table-header">
                    <span class="table-title">{{ message.table.title || 'Results' }}</span>
                    <span class="table-count">{{ message.table.rows.length }} rows</span>
                  </div>
                  <div class="table-wrapper">
                    <table class="data-table">
                      <thead>
                        <tr>
                          <th
                            v-for="col in message.table.columns"
                            :key="col.field"
                            @click="col.sortable !== false && sortTable(message, col.field)"
                            :class="{ sortable: col.sortable !== false, sorted: message.table.sortField === col.field }"
                          >
                            {{ col.label || col.field }}
                            <span v-if="col.sortable !== false" class="sort-icon">
                              <svg v-if="message.table.sortField === col.field && message.table.sortDir === 'asc'" width="12" height="12" viewBox="0 0 24 24" fill="none">
                                <path d="M12 5L12 19M12 5L6 11M12 5L18 11" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                              </svg>
                              <svg v-else-if="message.table.sortField === col.field && message.table.sortDir === 'desc'" width="12" height="12" viewBox="0 0 24 24" fill="none">
                                <path d="M12 19L12 5M12 19L6 13M12 19L18 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                              </svg>
                              <svg v-else width="12" height="12" viewBox="0 0 24 24" fill="none" style="opacity: 0.3">
                                <path d="M12 5L12 19M12 5L6 11M12 5L18 11" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                              </svg>
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(row, rIndex) in getSortedTableRows(message)" :key="rIndex">
                          <td v-for="col in message.table.columns" :key="col.field">
                            {{ formatTableCell(row[col.field], col.format) }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- Chart visualization -->
                <div v-if="message.chart" class="message-chart">
                  <apexchart
                    :type="message.chart.options.chart.type"
                    :options="message.chart.options"
                    :series="message.chart.series"
                    height="300"
                  />
                </div>
              </div>
            </div>

            <!-- Data freshness indicator -->
            <div v-if="message.role === 'assistant' && message.dataFreshness && !message.streaming" class="data-freshness">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                <path d="M12 6V12L16 14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
              Data as of {{ message.dataFreshness }}
            </div>

            <!-- Suggested follow-ups -->
            <div v-if="message.role === 'assistant' && message.suggestedFollowUps && message.suggestedFollowUps.length > 0 && !message.streaming" class="suggested-followups">
              <div class="followups-label">Suggested follow-ups</div>
              <div class="followups-list">
                <button
                  v-for="(followUp, fIndex) in message.suggestedFollowUps"
                  :key="fIndex"
                  type="button"
                  class="followup-button"
                  @click="sendFollowUp(followUp.prompt)"
                  :disabled="isLoading"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  {{ followUp.label }}
                </button>
              </div>
            </div>

            <!-- Action buttons (CSV, Notion) -->
            <div v-if="message.role === 'assistant' && (message.csv || message.conversationId) && !message.streaming" class="message-actions">
              <!-- CSV Download - Single export: direct download, Multiple: dropdown -->
              <div v-if="message.csv && message.csv.length > 0" class="csv-dropdown-container">
                <button
                  type="button"
                  class="action-button"
                  @click="message.csv.length === 1 ? downloadCsvExport(message.csv[0]) : toggleCsvDropdown(index)"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 3V15M12 15L7 10M12 15L17 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M3 17V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V17" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                  Download CSV
                  <span v-if="message.csv.length > 1" class="csv-count-badge">{{ message.csv.length }}</span>
                  <svg v-if="message.csv.length > 1" class="dropdown-chevron" :class="{ 'open': openCsvDropdownIndex === index }" width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
                <!-- Dropdown menu -->
                <div v-if="message.csv.length > 1 && openCsvDropdownIndex === index" class="csv-dropdown-menu">
                  <button
                    v-for="(csvExport, csvIndex) in message.csv"
                    :key="'csv-' + csvIndex"
                    type="button"
                    class="csv-dropdown-item"
                    @click="downloadCsvExport(csvExport); closeCsvDropdown()"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M14 2V8H20" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <span class="csv-dropdown-label">{{ csvExport.label }}</span>
                    <span v-if="csvExport.rowCount" class="csv-dropdown-count">{{ csvExport.rowCount }} rows</span>
                  </button>
                </div>
              </div>
              <button
                v-if="message.conversationId"
                type="button"
                class="action-button"
                :class="{ 'success': message.notionSynced }"
                :disabled="message.notionSynced"
                @click="openNotionModal(message)"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 4H20V20H4V4Z" stroke="currentColor" stroke-width="2"/>
                  <path d="M8 8V16L12 12L16 16V8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                {{ message.notionSynced ? 'Sent to Notion' : 'Send to Notion' }}
              </button>
              <small v-if="message.notionError" class="error-text">{{ message.notionError }}</small>
            </div>

            <!-- System warnings (token limits, turn counts, etc.) -->
            <div v-if="message.role === 'assistant' && message.warnings && message.warnings.length > 0 && !message.streaming" class="system-warnings">
              <div v-for="(warning, wIndex) in message.warnings" :key="'warning-' + wIndex" class="warning-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                  <path d="M12 8V12M12 16H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
                <span>{{ warning }}</span>
              </div>
            </div>

            <!-- Campaign Diagnostic Card -->
            <div v-if="message.role === 'assistant' && message.diagnostic && !message.streaming" class="diagnostic-card">
              <!-- Header with verdict and confidence -->
              <div class="diagnostic-header">
                <div class="verdict-badge" :class="getDiagnosticVerdictClass(message.diagnostic.recommendation)">
                  <svg v-if="getDiagnosticVerdictIcon(message.diagnostic.recommendation) === 'check'" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <svg v-else-if="getDiagnosticVerdictIcon(message.diagnostic.recommendation) === 'warning'" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 9V13M12 17H12.01M10.29 3.86L1.82 18C1.64 18.3 1.55 18.64 1.55 19C1.55 19.36 1.64 19.7 1.82 20C2 20.3 2.26 20.56 2.56 20.74C2.86 20.92 3.21 21.01 3.56 21.01H20.44C20.79 21.01 21.14 20.92 21.44 20.74C21.74 20.56 22 20.3 22.18 20C22.36 19.7 22.45 19.36 22.45 19C22.45 18.64 22.36 18.3 22.18 18L13.71 3.86C13.53 3.56 13.27 3.32 12.97 3.14C12.67 2.96 12.34 2.87 12 2.87C11.66 2.87 11.33 2.96 11.03 3.14C10.73 3.32 10.47 3.56 10.29 3.86Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                    <path d="M15 9L9 15M9 9L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                  <span>{{ formatDiagnosticVerdict(message.diagnostic.recommendation) }}</span>
                </div>
                <div class="confidence-badge" :class="message.diagnostic.confidence.level">
                  <div class="confidence-bar-bg">
                    <div class="confidence-bar-fill" :style="{ width: message.diagnostic.confidence.score + '%' }"></div>
                  </div>
                  <span class="confidence-text">{{ message.diagnostic.confidence.score }}% {{ message.diagnostic.confidence.level }}</span>
                </div>
              </div>

              <!-- Campaign info row -->
              <div class="diagnostic-info">
                <span class="campaign-id">Campaign #{{ message.diagnostic.campaignId }}</span>
                <span v-if="message.diagnostic.campaignDates.isHistorical" class="historical-tag">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                    <path d="M12 6V12L16 14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                  Historical (ended {{ message.diagnostic.campaignDates.daysEnded }} days ago)
                </span>
                <span v-else-if="message.diagnostic.campaignDates.start" class="date-range">
                  {{ formatDiagnosticDate(message.diagnostic.campaignDates.start) }} – {{ formatDiagnosticDate(message.diagnostic.campaignDates.end) }}
                </span>
              </div>

              <!-- Actions section -->
              <div v-if="message.diagnostic.actions && message.diagnostic.actions.length > 0" class="diagnostic-section">
                <div class="section-header">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 11L12 14L22 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M21 12V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H16" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <span>Recommended Actions</span>
                </div>
                <ul class="actions-list">
                  <li v-for="(action, aIdx) in (message.diagnostic.actions || [])" :key="'action-' + aIdx">{{ action }}</li>
                </ul>
              </div>

              <!-- Checks section -->
              <div class="diagnostic-section">
                <div class="section-header">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    <rect x="9" y="2" width="6" height="4" rx="1" stroke="currentColor" stroke-width="2"/>
                    <path d="M9 12L11 14L15 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <span>Diagnostic Checks</span>
                </div>

                <div class="checks-list">
                  <!-- Flight Dates Check -->
                  <div class="check-item" :class="getCheckStatusClass(message.diagnostic.checks.flightDates.status)">
                    <button type="button" class="check-header" @click="toggleDiagnosticCheck(index, 'flightDates')">
                      <div class="check-status-icon">
                        <svg v-if="message.diagnostic.checks.flightDates.status === 'ok'" width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <path d="M12 9V13M12 17H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                      </div>
                      <div class="check-info">
                        <span class="check-name">Flight Dates</span>
                        <span class="check-summary">
                          {{ message.diagnostic.checks.flightDates.flightCount }} flights
                          <template v-if="message.diagnostic.checks.flightDates.issues.length > 0">
                            · {{ message.diagnostic.checks.flightDates.issues.length }} issues
                          </template>
                        </span>
                      </div>
                      <svg class="check-chevron" :class="{ expanded: isDiagnosticCheckExpanded(index, 'flightDates') }" width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </button>
                    <div v-if="isDiagnosticCheckExpanded(index, 'flightDates') && (message.diagnostic.checks.flightDates.issues || []).length > 0" class="check-details">
                      <div v-for="(issue, iIdx) in (message.diagnostic.checks.flightDates.issues || [])" :key="'fd-' + iIdx" class="issue-item">
                        <span class="issue-ref">{{ issue.flightRef }}</span>
                        <span class="issue-text">{{ issue.issue }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Market Alignment Check -->
                  <div class="check-item" :class="getCheckStatusClass(message.diagnostic.checks.marketAlignment.status)">
                    <button type="button" class="check-header" @click="toggleDiagnosticCheck(index, 'marketAlignment')">
                      <div class="check-status-icon">
                        <svg v-if="message.diagnostic.checks.marketAlignment.status === 'ok'" width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <path d="M12 9V13M12 17H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                      </div>
                      <div class="check-info">
                        <span class="check-name">Market Alignment</span>
                        <span class="check-summary">
                          <template v-if="message.diagnostic.checks.marketAlignment.orphanedInventoryCount === 0">All aligned</template>
                          <template v-else>{{ message.diagnostic.checks.marketAlignment.orphanedInventoryCount }} orphaned</template>
                        </span>
                      </div>
                      <svg class="check-chevron" :class="{ expanded: isDiagnosticCheckExpanded(index, 'marketAlignment') }" width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </button>
                    <div v-if="isDiagnosticCheckExpanded(index, 'marketAlignment') && (message.diagnostic.checks.marketAlignment.orphanedInventory || []).length > 0" class="check-details">
                      <div v-for="(item, mIdx) in (message.diagnostic.checks.marketAlignment.orphanedInventory || [])" :key="'ma-' + mIdx" class="issue-item">
                        <span class="issue-ref">{{ item.matchableRef || item.MATCHABLE_REF }}</span>
                        <span class="issue-text">{{ item.inventoryType || item.INVENTORY_TYPE }} - Markets: {{ Array.isArray(item.itemMarkets || item.ITEM_MARKETS) ? (item.itemMarkets || item.ITEM_MARKETS).join(', ') : 'N/A' }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Historical Campaign Check -->
                  <div class="check-item" :class="message.diagnostic.checks.historicalCampaign.isHistorical ? 'check-warning' : 'check-ok'">
                    <button type="button" class="check-header" @click="toggleDiagnosticCheck(index, 'historical')">
                      <div class="check-status-icon">
                        <svg v-if="!message.diagnostic.checks.historicalCampaign.isHistorical" width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                          <path d="M12 6V12L16 14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                      </div>
                      <div class="check-info">
                        <span class="check-name">Historical Campaign</span>
                        <span class="check-summary">
                          <template v-if="message.diagnostic.checks.historicalCampaign.isHistorical">Ended {{ message.diagnostic.checks.historicalCampaign.daysEnded }} days ago</template>
                          <template v-else>Active or recent</template>
                        </span>
                      </div>
                      <svg class="check-chevron" :class="{ expanded: isDiagnosticCheckExpanded(index, 'historical') }" width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </button>
                    <div v-if="isDiagnosticCheckExpanded(index, 'historical') && message.diagnostic.checks.historicalCampaign.implication" class="check-details">
                      <p class="implication-text">{{ message.diagnostic.checks.historicalCampaign.implication }}</p>
                    </div>
                  </div>

                  <!-- Device Coverage Check -->
                  <div class="check-item" :class="getCheckStatusClass(message.diagnostic.checks.deviceCoverage.status)">
                    <button type="button" class="check-header" @click="toggleDiagnosticCheck(index, 'deviceCoverage')">
                      <div class="check-status-icon">
                        <svg v-if="message.diagnostic.checks.deviceCoverage.status === 'ok'" width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <path d="M12 9V13M12 17H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                      </div>
                      <div class="check-info">
                        <span class="check-name">Device Coverage</span>
                        <span class="check-summary">
                          {{ message.diagnostic.checks.deviceCoverage.sampleCoveragePercent || 0 }}% coverage
                          <template v-if="message.diagnostic.checks.deviceCoverage.inventoryWithGapsCount > 0">
                            · {{ message.diagnostic.checks.deviceCoverage.inventoryWithGapsCount }} with gaps
                          </template>
                        </span>
                      </div>
                      <svg class="check-chevron" :class="{ expanded: isDiagnosticCheckExpanded(index, 'deviceCoverage') }" width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </button>
                    <div v-if="isDiagnosticCheckExpanded(index, 'deviceCoverage')" class="check-details">
                      <div class="coverage-stats">
                        <div class="stat-item">
                          <span class="stat-label">Total Inventory</span>
                          <span class="stat-value">{{ message.diagnostic.checks.deviceCoverage.totalInventoryInCampaign }}</span>
                        </div>
                        <div class="stat-item">
                          <span class="stat-label">Sampled</span>
                          <span class="stat-value">{{ message.diagnostic.checks.deviceCoverage.inventorySampled }}</span>
                        </div>
                        <div class="stat-item">
                          <span class="stat-label">No Data</span>
                          <span class="stat-value">{{ message.diagnostic.checks.deviceCoverage.inventoryWithNoDataCount }}</span>
                        </div>
                        <div class="stat-item">
                          <span class="stat-label">With Gaps</span>
                          <span class="stat-value">{{ message.diagnostic.checks.deviceCoverage.inventoryWithGapsCount }}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Rematch Prerequisites Check -->
                  <div class="check-item" :class="getCheckStatusClass(message.diagnostic.checks.rematchPrerequisites.status)">
                    <button type="button" class="check-header" @click="toggleDiagnosticCheck(index, 'rematchPrereqs')">
                      <div class="check-status-icon">
                        <svg v-if="message.diagnostic.checks.rematchPrerequisites.status === 'ok'" width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <path d="M12 9V13M12 17H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                      </div>
                      <div class="check-info">
                        <span class="check-name">Rematch Prerequisites</span>
                        <span class="check-summary">
                          {{ message.diagnostic.checks.rematchPrerequisites.checked }} checked
                          <template v-if="message.diagnostic.checks.rematchPrerequisites.issues.length > 0">
                            · {{ message.diagnostic.checks.rematchPrerequisites.issues.length }} issues
                          </template>
                        </span>
                      </div>
                      <svg class="check-chevron" :class="{ expanded: isDiagnosticCheckExpanded(index, 'rematchPrereqs') }" width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </button>
                    <div v-if="isDiagnosticCheckExpanded(index, 'rematchPrereqs') && (message.diagnostic.checks.rematchPrerequisites.issues || []).length > 0" class="check-details">
                      <div v-for="(issue, pIdx) in (message.diagnostic.checks.rematchPrerequisites.issues || [])" :key="'prereq-' + pIdx" class="issue-item">
                        <span class="issue-ref">{{ issue.matchableRef }}</span>
                        <span class="issue-text">{{ issue.issue }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Inventory Gaps Table -->
              <div v-if="message.diagnostic.checks.deviceCoverage.inventoryGapsList && message.diagnostic.checks.deviceCoverage.inventoryGapsList.length > 0" class="diagnostic-section">
                <button type="button" class="section-header clickable" @click="toggleDiagnosticCheck(index, 'gapsTable')">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 10H21M3 14H21M12 3V21M5 3H19C20.1046 3 21 3.89543 21 5V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V5C3 3.89543 3.89543 3 5 3Z" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                  <span>Inventory with Data Gaps</span>
                  <span class="section-count">{{ message.diagnostic.checks.deviceCoverage.inventoryGapsList.length }}</span>
                  <svg class="section-chevron" :class="{ expanded: isDiagnosticCheckExpanded(index, 'gapsTable') }" width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
                <div v-if="isDiagnosticCheckExpanded(index, 'gapsTable')" class="diagnostic-table-wrapper">
                  <table class="diagnostic-table">
                    <thead>
                      <tr>
                        <th>Reference</th>
                        <th>Type</th>
                        <th>Days with Data</th>
                        <th>Gap Days</th>
                        <th>Last Data</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="inv in (message.diagnostic.checks.deviceCoverage.inventoryGapsList || [])" :key="'gap-' + inv.matchableId">
                        <td class="ref-cell">{{ inv.matchableRef }}</td>
                        <td>{{ inv.type }}</td>
                        <td>{{ inv.daysWithData }} / {{ inv.totalDays }}</td>
                        <td class="gap-cell">{{ inv.gapDays }}</td>
                        <td>{{ formatDiagnosticDate(inv.lastDataDate) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Confidence Factors -->
              <div v-if="message.diagnostic.confidence.factors && message.diagnostic.confidence.factors.length > 0" class="diagnostic-section">
                <button type="button" class="section-header clickable" @click="toggleDiagnosticCheck(index, 'factors')">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                    <path d="M12 16V12M12 8H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                  <span>Analysis Notes</span>
                  <svg class="section-chevron" :class="{ expanded: isDiagnosticCheckExpanded(index, 'factors') }" width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
                <ul v-if="isDiagnosticCheckExpanded(index, 'factors')" class="factors-list">
                  <li v-for="(factor, fIdx) in (message.diagnostic.confidence.factors || [])" :key="'factor-' + fIdx">{{ factor }}</li>
                </ul>
              </div>
            </div>

            <!-- Dip Diagnostic Card -->
            <div v-if="message.role === 'assistant' && message.dipDiagnostic && !message.streaming" class="dip-diagnostic-card">
              <!-- Header with verdict and confidence -->
              <div class="dip-header">
                <div class="dip-verdict-badge" :class="getDipVerdictClass(message.dipDiagnostic.recommendation)">
                  <svg v-if="getDipVerdictIcon(message.dipDiagnostic.recommendation) === 'check'" width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <svg v-else-if="getDipVerdictIcon(message.dipDiagnostic.recommendation) === 'info'" width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                    <path d="M12 16V12M12 8H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                  <svg v-else-if="getDipVerdictIcon(message.dipDiagnostic.recommendation) === 'warning'" width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M12 9V13M12 17H12.01M10.29 3.86L1.82 18C1.64 18.3 1.55 18.64 1.55 19C1.55 19.36 1.64 19.7 1.82 20C2 20.3 2.26 20.56 2.56 20.74C2.86 20.92 3.21 21.01 3.56 21.01H20.44C20.79 21.01 21.14 20.92 21.44 20.74C21.74 20.56 22 20.3 22.18 20C22.36 19.7 22.45 19.36 22.45 19C22.45 18.64 22.36 18.3 22.18 18L13.71 3.86C13.53 3.56 13.27 3.32 12.97 3.14C12.67 2.96 12.34 2.87 12 2.87C11.66 2.87 11.33 2.96 11.03 3.14C10.73 3.32 10.47 3.56 10.29 3.86Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <svg v-else-if="getDipVerdictIcon(message.dipDiagnostic.recommendation) === 'error'" width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                    <path d="M15 9L9 15M9 9L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                  <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <circle cx="11" cy="11" r="8" stroke="currentColor" stroke-width="2"/>
                    <path d="M21 21L16.65 16.65" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                  <span>{{ formatDipVerdict(message.dipDiagnostic.recommendation) }}</span>
                </div>
                <div class="dip-confidence" :class="message.dipDiagnostic.confidence.level">
                  <div class="confidence-bar-bg">
                    <div class="confidence-bar-fill" :style="{ width: message.dipDiagnostic.confidence.score + '%' }"></div>
                  </div>
                  <span class="confidence-text">{{ message.dipDiagnostic.confidence.score }}% {{ message.dipDiagnostic.confidence.level }}</span>
                </div>
              </div>

              <!-- Campaign & Analysis Window info -->
              <div class="dip-info-row">
                <span class="dip-campaign-id">{{ message.dipDiagnostic.campaignRef || 'Campaign #' + message.dipDiagnostic.campaignId }}</span>
                <span v-if="message.dipDiagnostic.analysisWindow.daysAnalyzed" class="dip-analysis-window">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" stroke-width="2"/>
                    <path d="M16 2V6M8 2V6M3 10H21" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                  {{ message.dipDiagnostic.analysisWindow.daysAnalyzed }} days analyzed
                </span>
              </div>

              <!-- Interpretation summary from meta -->
              <div v-if="message.dipDiagnostic.meta && message.dipDiagnostic.meta.interpretation" class="dip-summary">
                {{ message.dipDiagnostic.meta.interpretation.summary }}
              </div>

              <!-- Anomalies Table -->
              <div v-if="message.dipDiagnostic.anomalies && message.dipDiagnostic.anomalies.length > 0" class="dip-section">
                <button type="button" class="dip-section-header clickable" @click="toggleDiagnosticCheck(index, 'dipAnomalies')">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M22 12H18L15 21L9 3L6 12H2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <span>Detected Anomalies</span>
                  <span class="dip-section-count">{{ message.dipDiagnostic.anomalies.length }}</span>
                  <svg class="dip-chevron" :class="{ expanded: isDiagnosticCheckExpanded(index, 'dipAnomalies') }" width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
                <div v-if="isDiagnosticCheckExpanded(index, 'dipAnomalies')" class="dip-table-wrapper">
                  <table class="dip-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Actual</th>
                        <th>Expected</th>
                        <th>Z-Score</th>
                        <th>Severity</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(anomaly, aIdx) in (message.dipDiagnostic.anomalies || [])" :key="'anomaly-' + aIdx" :class="getAnomalySeverityClass(anomaly.severity)">
                        <td>{{ formatDiagnosticDate(anomaly.date) }}</td>
                        <td>{{ anomaly.impressions?.toLocaleString() || '-' }}</td>
                        <td>{{ anomaly.expectedImpressions?.toLocaleString() || '-' }}</td>
                        <td class="zscore-cell">{{ formatZScore(anomaly.zScore) }}</td>
                        <td>
                          <span class="severity-badge" :class="anomaly.severity">{{ anomaly.severity }}</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Inventory Health Card (if significant drop) -->
              <div v-if="message.dipDiagnostic.findings.inventory && message.dipDiagnostic.findings.inventory.dropPercent >= 20" class="dip-inventory-alert">
                <div class="inventory-alert-header">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M21 16V8C21 6.89543 20.1046 6 19 6H5C3.89543 6 3 6.89543 3 8V16C3 17.1046 3.89543 18 5 18H19C20.1046 18 21 17.1046 21 16Z" stroke="currentColor" stroke-width="2"/>
                    <path d="M3 10H21M8 6V18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                  <span>Inventory Health Issue</span>
                </div>
                <div class="inventory-alert-stats">
                  <div class="inv-stat">
                    <span class="inv-stat-value">{{ message.dipDiagnostic.findings.inventory.matchablesBefore }}</span>
                    <span class="inv-stat-label">Before</span>
                  </div>
                  <div class="inv-stat-arrow">→</div>
                  <div class="inv-stat">
                    <span class="inv-stat-value">{{ message.dipDiagnostic.findings.inventory.matchablesAfter }}</span>
                    <span class="inv-stat-label">After</span>
                  </div>
                  <div class="inv-stat-drop">
                    <span class="drop-value">-{{ message.dipDiagnostic.findings.inventory.dropPercent }}%</span>
                    <span class="drop-label">{{ message.dipDiagnostic.findings.inventory.matchablesStopped }} stopped</span>
                  </div>
                </div>
              </div>

              <!-- Market Breakdown Table -->
              <div v-if="message.dipDiagnostic.findings.markets.breakdown && message.dipDiagnostic.findings.markets.breakdown.length > 0" class="dip-section">
                <button type="button" class="dip-section-header clickable" @click="toggleDiagnosticCheck(index, 'dipMarkets')">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" stroke-width="2"/>
                    <path d="M2 12H22M12 2C14.5013 4.73835 15.9228 8.29203 16 12C15.9228 15.708 14.5013 19.2616 12 22C9.49872 19.2616 8.07725 15.708 8 12C8.07725 8.29203 9.49872 4.73835 12 2Z" stroke="currentColor" stroke-width="2"/>
                  </svg>
                  <span>Market Breakdown</span>
                  <span v-if="message.dipDiagnostic.findings.markets.significantDrops.length > 0" class="dip-section-count dip-count-warning">{{ message.dipDiagnostic.findings.markets.significantDrops.length }} drops</span>
                  <svg class="dip-chevron" :class="{ expanded: isDiagnosticCheckExpanded(index, 'dipMarkets') }" width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
                <div v-if="isDiagnosticCheckExpanded(index, 'dipMarkets')" class="dip-table-wrapper">
                  <table class="dip-table">
                    <thead>
                      <tr>
                        <th>Market</th>
                        <th>Before</th>
                        <th>After</th>
                        <th>Change</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="market in (message.dipDiagnostic.findings.markets.breakdown || [])" :key="'market-' + market.marketId" :class="{ 'significant-drop': market.isSignificantDrop }">
                        <td class="market-name-cell">{{ market.marketName }}</td>
                        <td>{{ market.impressionsBefore?.toLocaleString() || '-' }}</td>
                        <td>{{ market.impressionsAfter?.toLocaleString() || '-' }}</td>
                        <td class="change-cell" :class="{ 'negative': market.changePercent < 0 }">{{ formatChangePercent(market.changePercent) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Findings Summary (collapsible sections) -->
              <div class="dip-section" v-if="message.dipDiagnostic.findings.timing.length > 0 || message.dipDiagnostic.findings.dayOfWeek.length > 0 || message.dipDiagnostic.findings.inventory.findings?.length > 0">
                <button type="button" class="dip-section-header clickable" @click="toggleDiagnosticCheck(index, 'dipFindings')">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M14 2V8H20M16 13H8M16 17H8M10 9H8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <span>Analysis Findings</span>
                  <svg class="dip-chevron" :class="{ expanded: isDiagnosticCheckExpanded(index, 'dipFindings') }" width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
                <div v-if="isDiagnosticCheckExpanded(index, 'dipFindings')" class="dip-findings-content">
                  <div v-if="(message.dipDiagnostic.findings.timing || []).length > 0" class="findings-group">
                    <div class="findings-group-title">Timing Analysis</div>
                    <ul class="findings-list">
                      <li v-for="(item, tIdx) in (message.dipDiagnostic.findings.timing || [])" :key="'timing-' + tIdx">{{ item }}</li>
                    </ul>
                  </div>
                  <div v-if="(message.dipDiagnostic.findings.dayOfWeek || []).length > 0" class="findings-group">
                    <div class="findings-group-title">Day-of-Week Patterns</div>
                    <ul class="findings-list">
                      <li v-for="(item, dIdx) in (message.dipDiagnostic.findings.dayOfWeek || [])" :key="'dow-' + dIdx">{{ item }}</li>
                    </ul>
                  </div>
                  <div v-if="message.dipDiagnostic.findings.inventory && (message.dipDiagnostic.findings.inventory.findings || []).length > 0" class="findings-group">
                    <div class="findings-group-title">Inventory Issues</div>
                    <ul class="findings-list">
                      <li v-for="(item, iIdx) in (message.dipDiagnostic.findings.inventory.findings || [])" :key="'inv-' + iIdx">{{ item }}</li>
                    </ul>
                  </div>
                </div>
              </div>

              <!-- Actions -->
              <div v-if="message.dipDiagnostic.actions && message.dipDiagnostic.actions.length > 0" class="dip-section dip-actions-section">
                <div class="dip-section-header">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M9 11L12 14L22 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M21 12V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V5C3 3.89543 3.89543 3 5 3H16" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <span>Recommended Actions</span>
                </div>
                <ul class="dip-actions-list">
                  <li v-for="(action, actIdx) in (message.dipDiagnostic.actions || [])" :key="'dip-action-' + actIdx">{{ action }}</li>
                </ul>
              </div>

              <!-- Confidence Factors -->
              <div v-if="message.dipDiagnostic.confidence.factors && message.dipDiagnostic.confidence.factors.length > 0" class="dip-section">
                <button type="button" class="dip-section-header clickable" @click="toggleDiagnosticCheck(index, 'dipFactors')">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                    <path d="M12 16V12M12 8H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                  <span>Confidence Factors</span>
                  <svg class="dip-chevron" :class="{ expanded: isDiagnosticCheckExpanded(index, 'dipFactors') }" width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
                <ul v-if="isDiagnosticCheckExpanded(index, 'dipFactors')" class="dip-factors-list">
                  <li v-for="(factor, fIdx) in (message.dipDiagnostic.confidence.factors || [])" :key="'dip-factor-' + fIdx">{{ factor }}</li>
                </ul>
              </div>
            </div>

            <!-- Consolidated SQL Queries Section (from all tools) -->
            <div v-if="message.role === 'assistant' && message.allQueries && message.allQueries.length > 0 && !message.streaming" class="sql-queries-card">
              <button type="button" class="sql-queries-header" @click="toggleDiagnosticCheck(index, 'allSqlQueries')">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M4 7V4H20V7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M9 20H15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M12 4V20" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span>SQL Queries</span>
                <span class="sql-queries-total-count">{{ getTotalQueryCount(message.allQueries) }}</span>
                <svg class="sql-queries-chevron" :class="{ expanded: isDiagnosticCheckExpanded(index, 'allSqlQueries') }" width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
              <div v-if="isDiagnosticCheckExpanded(index, 'allSqlQueries')" class="sql-queries-body">
                <div v-for="(toolQueries, tIdx) in message.allQueries" :key="'tool-queries-' + tIdx" class="sql-tool-group">
                  <div class="sql-tool-name">{{ formatToolName(toolQueries.toolName) }}</div>
                  <div v-for="(query, qIdx) in (toolQueries.queries || [])" :key="'query-' + tIdx + '-' + qIdx" class="sql-query-block">
                    <div class="sql-query-header">
                      <span class="sql-query-name">{{ query.name }}</span>
                      <button type="button" class="sql-copy-btn" @click="copySqlToClipboard(query.sql, $event)">Copy</button>
                    </div>
                    <pre class="sql-code"><code>{{ query.sql }}</code></pre>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Input area -->
    <div class="input-section">
      <!-- Quick Prompt Chips (visible when no messages or input is empty/not focused) -->
      <div v-if="showQuickChips" class="quick-chips-container">
        <div class="quick-chips">
          <button
            v-for="chip in visibleQuickPrompts"
            :key="chip.label"
            type="button"
            class="quick-chip"
            @click="useQuickPrompt(chip.prompt)"
          >
            {{ chip.label }}
          </button>
          <button
            type="button"
            class="quick-chip more-chip"
            @click="$emit('open-help')"
          >
            More...
          </button>
        </div>
      </div>

      <form @submit.prevent="sendMessage" class="input-form">
        <div class="input-wrapper-container">
          <!-- Focus Tooltip -->
          <div v-if="showInputTooltip && !userMessage.trim()" class="input-tooltip">
            <div class="tooltip-header">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 18V12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12V18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <circle cx="12" cy="5" r="1" fill="currentColor"/>
              </svg>
              <span>Try asking:</span>
            </div>
            <ul class="tooltip-examples">
              <li
                v-for="(example, idx) in tooltipExamples"
                :key="idx"
                @click="useQuickPrompt(example)"
              >
                "{{ example }}"
              </li>
            </ul>
            <button type="button" class="tooltip-see-all" @click="$emit('open-help')">
              See all examples
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>

          <div class="input-wrapper">
            <textarea
              v-model="userMessage"
              class="message-input"
              placeholder="Message Atlas..."
              :disabled="isLoading"
              rows="1"
              @keydown.enter.exact.prevent="sendMessage"
              @input="autoResizeInput"
              @focus="handleInputFocus"
              @blur="handleInputBlur"
              ref="messageInput"
            ></textarea>
            <button
              type="button"
              class="help-button"
              @click="$emit('open-help')"
              title="What can Atlas help with?"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                <path d="M12 16V12M12 8H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
            <button
              type="submit"
              class="send-button"
              :disabled="!userMessage.trim() || isLoading"
              :class="{ 'active': userMessage.trim() && !isLoading }"
            >
              <span v-if="isLoading" class="spinner"></span>
              <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 2L11 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      </form>
      <div class="input-footer">
        Atlas can make mistakes. Verify important information.
      </div>
    </div>
  </div>

  <!-- Notion Note Modal -->
  <div v-if="notionModalVisible" class="modal-overlay" @click.self="closeNotionModal">
    <div class="modal-container">
      <div class="modal-header">
        <h3 class="modal-title">Add note for Notion</h3>
        <button type="button" class="modal-close" @click="closeNotionModal">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
      </div>
      <div class="modal-body">
        <p class="modal-description">This note will be attached to the Notion entry for this conversation.</p>
        <textarea
          v-model="notionModalMessage"
          class="modal-textarea"
          rows="5"
          placeholder="Optional: context, highlights, or follow-ups..."
          :disabled="notionModalSending"
        ></textarea>
        <div v-if="notionModalError" class="modal-error">{{ notionModalError }}</div>
      </div>
      <div class="modal-footer">
        <button type="button" class="modal-button secondary" @click="closeNotionModal" :disabled="notionModalSending">Cancel</button>
        <button type="button" class="modal-button primary" @click="confirmSendToNotion" :disabled="notionModalSending">
          <span v-if="notionModalSending" class="spinner"></span>
          Send to Notion
        </button>
      </div>
    </div>
  </div>

  <!-- Toolkit Modal -->
  <AtlasToolkitModal
    :visible="showToolkitModal"
    :conversationId="conversationId"
    @close="showToolkitModal = false"
    @execute="handleRecipeExecute"
  />
</template>

<script>
import InsightsService from '@/services/InsightsService';
import { isString } from 'lodash';
import { parseMarkdown } from '@/utils/markdown.js';
import AppUtil from '@/utils/AppUtil.js';
import VueApexCharts from 'vue3-apexcharts';
import { ATLAS_QUICK_PROMPTS, ATLAS_TOOLTIP_EXAMPLES } from '@/data/atlasHelpData.js';
import AtlasToolkitModal from '@/components/AtlasToolkitModal.vue';

export default {
  name: 'AiChatPanel',
  components: {
    apexchart: VueApexCharts,
    AtlasToolkitModal,
  },
  props: {
    title: {
      type: String,
      default: 'Atlas'
    },
    contextIds: {
      type: Array,
      default: () => []
    },
    customButtons: {
      type: Array,
      default: () => []
    }
  },
  emits: ['open-help'],
  data() {
    return {
      messages: [],
      userMessage: '',
      systemPrompt: '',
      isLoading: false,
      showSystemPrompt: false,
      insightsService: new InsightsService(),
      currentStreamController: null,
      appUtil: new AppUtil(),
      // Help UI state
      quickPrompts: ATLAS_QUICK_PROMPTS,
      tooltipExamples: ATLAS_TOOLTIP_EXAMPLES,
      showInputTooltip: false,
      inputFocused: false,
      // Toolkit state
      showToolkitModal: false,
      isExecutingRecipe: false,
      loadingPhraseIntervalId: null,
      loadingDotsIntervalId: null,
      selectedAvatar: 'none',
      avatarMenuOpen: false,
      conversationId: null,
      notionModalVisible: false,
      notionModalMessage: '',
      notionModalSending: false,
      notionModalError: null,
      notionTargetMessage: null,
      openCsvDropdownIndex: null,
      expandedToolResults: {}, // Track which message indices have expanded tool results
      expandedDiagnosticChecks: {}, // Track which diagnostic checks are expanded per message { messageIndex: { checkName: boolean } }
      loadingPhrases: [
        'Repainting your billboards',
        'Counting impressions',
        'Analyzing foot traffic',
        'Surveying the landscape',
        'Mapping your markets',
        'Crunching the numbers',
        'Measuring viewability',
        'Tracking your fleet',
        'Calculating reach',
        'Optimizing placements',
        'Scanning the horizon',
        'Aggregating data points',
        'Processing geofences',
        'Evaluating dwell times',
        'Assessing visibility',
        'Compiling market reports',
        'Analyzing traffic patterns',
        'Measuring brand lift',
        'Calibrating the panels',
        'Reviewing campaign metrics',
        'Polling the billboards',
        'Inspecting inventory',
        'Gathering insights',
        'Running the numbers',
        'Validating impressions',
        'Checking coverage',
        'Mapping routes',
        'Analyzing demographics',
        'Measuring frequency',
        'Calculating GRPs',
        'Reviewing placements',
        'Assessing performance',
        'Processing location data',
        'Evaluating exposure',
        'Compiling analytics',
        'Surveying audiences',
        'Tracking mobile signals',
        'Measuring OOH impact',
        'Analyzing points of interest',
        'Reviewing market data',
        'Processing venue data',
        'Gathering attribution data',
        'Running attribution models',
        'Analyzing conversion paths',
        'Measuring store visits',
        'Tracking campaign lift',
        'Processing panel data',
        'Optimizing media mix',
        'Warming up the vinyl',
        'Polishing the pixels'
      ]
    };
  },
  computed: {
    showQuickChips() {
      // Show chips when there are no messages or when the input is empty and not focused
      return this.messages.length === 0 || (!this.userMessage.trim() && !this.inputFocused && !this.isLoading);
    },
    visibleQuickPrompts() {
      // Show first 5 quick prompts
      return this.quickPrompts.slice(0, 5);
    },
  },
  watch: {
    customButtons: {
      handler(newButtons) {
        this.validateCustomButtons();
      },
      immediate: true
    },
    userMessage(newVal) {
      // Hide tooltip when user starts typing
      if (newVal.trim()) {
        this.showInputTooltip = false;
      }
    },
  },
  mounted() {
    this.handleClickOutside = (event) => {
      if (this.openCsvDropdownIndex !== null) {
        // Check if click is outside any csv dropdown container
        const clickedDropdown = event.target.closest('.csv-dropdown-container');
        if (!clickedDropdown) {
          this.closeCsvDropdown();
        }
      }
    };
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    if (this.handleClickOutside) {
      document.removeEventListener('click', this.handleClickOutside);
    }
  },
  methods: {
    // Help UI methods
    handleInputFocus() {
      this.inputFocused = true;
      // Show tooltip after a short delay if input is still empty
      setTimeout(() => {
        if (this.inputFocused && !this.userMessage.trim()) {
          this.showInputTooltip = true;
        }
      }, 300);
    },
    handleInputBlur() {
      this.inputFocused = false;
      // Hide tooltip after a delay (allows clicking on tooltip items)
      setTimeout(() => {
        if (!this.inputFocused) {
          this.showInputTooltip = false;
        }
      }, 200);
    },
    useQuickPrompt(prompt) {
      this.userMessage = prompt;
      this.showInputTooltip = false;
      this.$nextTick(() => {
        if (this.$refs.messageInput) {
          this.$refs.messageInput.focus();
          this.autoResizeInput();
        }
      });
    },
    setUserMessage(message) {
      this.userMessage = message;
      this.$nextTick(() => {
        if (this.$refs.messageInput) {
          this.$refs.messageInput.focus();
          this.autoResizeInput();
        }
      });
    },
    async handleRecipeExecute({ recipeId, recipeName, params }) {
      if (this.isLoading || this.isExecutingRecipe) return;

      this.isExecutingRecipe = true;
      this.showToolkitModal = false;

      // Format params for display
      const paramDisplay = Object.entries(params)
        .map(([key, value]) => `${key}: ${value}`)
        .join(', ');

      // Add a special "recipe" user message
      this.messages.push({
        role: 'user',
        content: `Run recipe: ${recipeName}`,
        isRecipe: true,
        recipeId,
        recipeName,
        recipeParams: params,
        recipeParamDisplay: paramDisplay,
      });

      this.scrollToBottom();

      // Show loading state with animated loading message
      this.isLoading = true;
      let currentBasePhrase = this.getRandomPhrase();
      const loadingMessage = {
        role: 'assistant',
        content: currentBasePhrase,
        streaming: false,
        isLoadingPhrase: true,
        phraseFading: false,
      };
      this.messages.push(loadingMessage);
      const loadingMessageIndex = this.messages.length - 1;
      this.scrollToBottom();

      // Animate dots
      let dotCount = 0;
      const dotsInterval = setInterval(() => {
        const msg = this.messages[loadingMessageIndex];
        if (!msg || !msg.isLoadingPhrase || msg.phraseFading) return;
        dotCount = (dotCount + 1) % 4;
        msg.content = currentBasePhrase + '.'.repeat(dotCount);
      }, 400);

      // Rotate phrases
      const phraseInterval = setInterval(() => {
        const msg = this.messages[loadingMessageIndex];
        if (!msg || !msg.isLoadingPhrase) return;
        msg.phraseFading = true;
        setTimeout(() => {
          if (this.messages[loadingMessageIndex]?.isLoadingPhrase) {
            currentBasePhrase = this.getRandomPhrase();
            dotCount = 0;
            this.messages[loadingMessageIndex].content = currentBasePhrase;
            this.messages[loadingMessageIndex].phraseFading = false;
          }
        }, 300);
      }, 5000);

      const clearLoadingIntervals = () => {
        clearInterval(dotsInterval);
        clearInterval(phraseInterval);
      };

      try {
        const response = await this.insightsService.executeRecipe(
          recipeId,
          params,
          this.conversationId
        );

        clearLoadingIntervals();

        // Remove loading message
        this.messages.splice(loadingMessageIndex, 1);

        // Update conversation ID if returned
        if (response.conversationId) {
          this.conversationId = response.conversationId;
        }

        // Add assistant response
        const assistantMessage = {
          role: 'assistant',
          content: response.response || response.content || '',
          streaming: false,
        };

        // Extract and attach table if present
        const table = this.extractTableFromResponse(response);
        if (table) {
          assistantMessage.table = table;
        }

        // Extract and attach chart if present
        const chart = this.extractChartFromResponse(response);
        if (chart) {
          assistantMessage.chart = chart;
        }

        // Extract and attach CSV exports if present
        const csvExports = this.extractCsvFromResponse(response);
        if (csvExports) {
          assistantMessage.csvExports = csvExports;
        }

        // Extract interpretation if present
        const interpretation = this.extractInterpretationFromResponse(response);
        if (interpretation) {
          assistantMessage.interpretation = interpretation;
        }

        // Extract diagnostics if present
        const diagnostic = this.extractDiagnosticFromResponse(response);
        if (diagnostic) {
          assistantMessage.diagnostic = diagnostic;
        }

        // Extract dip diagnostics if present
        const dipDiagnostic = this.extractDipDiagnosticFromResponse(response);
        if (dipDiagnostic) {
          assistantMessage.dipDiagnostic = dipDiagnostic;
        }

        // Extract SQL queries if present
        const allQueries = this.extractAllQueriesFromResponse(response);
        if (allQueries && allQueries.length > 0) {
          assistantMessage.allQueries = allQueries;
        }

        // Extract suggested follow-ups
        const suggestedFollowUps = this.extractSuggestedFollowUps(response);
        if (suggestedFollowUps && suggestedFollowUps.length > 0) {
          assistantMessage.suggestedFollowUps = suggestedFollowUps;
        }

        // Handle warnings
        if (response.warnings && response.warnings.length > 0) {
          assistantMessage.warnings = response.warnings;
        }

        // Handle context info
        if (response.contextInfo) {
          assistantMessage.contextInfo = response.contextInfo;
        }

        this.messages.push(assistantMessage);
      } catch (error) {
        clearLoadingIntervals();
        // Remove loading message
        if (this.messages[loadingMessageIndex]?.isLoadingPhrase) {
          this.messages.splice(loadingMessageIndex, 1);
        }
        console.error('Recipe execution error:', error);

        this.messages.push({
          role: 'assistant',
          content: `Sorry, I encountered an error executing the recipe: ${error.message || 'Unknown error'}`,
          streaming: false,
          isError: true,
        });
      } finally {
        this.isLoading = false;
        this.isExecutingRecipe = false;
        this.scrollToBottom();
      }
    },
    parseMarkdown(text) {
      return parseMarkdown(text);
    },
    setAvatar(avatar) {
      this.selectedAvatar = avatar || 'none';
      this.avatarMenuOpen = false;
    },
    autoResizeInput() {
      const textarea = this.$refs.messageInput;
      if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
      }
    },
    async startWithPrompt(prompt) {
      const userPrompt = (prompt || '').trim();
      if (!userPrompt || this.isLoading) return;
      this.messages.push({ role: 'user', content: userPrompt });
      await this.sendToAI(userPrompt);
    },
    sendFollowUp(prompt) {
      if (!prompt || this.isLoading) return;
      this.userMessage = prompt;
      this.sendMessage();
    },
    copyToClipboard(message, index) {
      const text = message.content || '';
      navigator.clipboard.writeText(text).then(() => {
        // Show feedback
        this.$set ? this.$set(message, 'copied', true) : (message.copied = true);
        setTimeout(() => {
          message.copied = false;
        }, 2000);
      }).catch(err => {
        console.error('Failed to copy:', err);
      });
    },
    sortTable(message, field) {
      if (!message.table) return;
      if (message.table.sortField === field) {
        // Toggle direction
        message.table.sortDir = message.table.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        message.table.sortField = field;
        message.table.sortDir = 'asc';
      }
    },
    getSortedTableRows(message) {
      if (!message.table || !message.table.rows) return [];
      const rows = [...message.table.rows];
      if (message.table.sortField) {
        const field = message.table.sortField;
        const dir = message.table.sortDir === 'desc' ? -1 : 1;
        rows.sort((a, b) => {
          let aVal = a[field];
          let bVal = b[field];
          // Handle numbers
          if (typeof aVal === 'number' && typeof bVal === 'number') {
            return (aVal - bVal) * dir;
          }
          // Handle strings
          aVal = String(aVal || '').toLowerCase();
          bVal = String(bVal || '').toLowerCase();
          if (aVal < bVal) return -1 * dir;
          if (aVal > bVal) return 1 * dir;
          return 0;
        });
      }
      return rows;
    },
    formatTableCell(value, format) {
      if (value === null || value === undefined) return '-';
      if (format === 'number') {
        const num = Number(value);
        if (isNaN(num)) return value;
        return num.toLocaleString();
      }
      if (format === 'currency') {
        const num = Number(value);
        if (isNaN(num)) return value;
        return '$' + num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
      if (format === 'percent') {
        const num = Number(value);
        if (isNaN(num)) return value;
        return num.toFixed(1) + '%';
      }
      if (format === 'date') {
        const date = new Date(value);
        if (isNaN(date.getTime())) return value;
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      }
      return String(value);
    },
    extractAssistantText(response) {
      if (response == null) return '';
      if (typeof response === 'string') return response;
      if (typeof response === 'object') {
        if (response.success && response.data && typeof response.data.response === 'string') {
          return response.data.response;
        }
        if (typeof response.response === 'string') return response.response;
        if (typeof response.content === 'string') return response.content;
        if (response.content && typeof response.content.text === 'string') return response.content.text;
        if (typeof response.message === 'string') return response.message;
        if (response.data) {
          if (typeof response.data.response === 'string') return response.data.response;
          if (typeof response.data.content === 'string') return response.data.content;
          if (typeof response.data.message === 'string') return response.data.message;
          if (response.data.text && typeof response.data.text === 'string') return response.data.text;
        }
        if (typeof response.text === 'string') return response.text;
        try {
          return JSON.stringify(response);
        } catch (e) {
          return String(response);
        }
      }
      return String(response);
    },
    extractCsvFromResponse(response) {
      try {
        const data = response && response.data ? response.data : response;
        const toolResults = data && Array.isArray(data.toolResults) ? data.toolResults : [];
        const exports = [];

        // Collect all exportable tool results
        toolResults.forEach((tr, index) => {
          if (tr && tr.result && tr.result.exportable) {
            const { exportable } = tr.result;
            const dataPath = exportable.path || 'rows';
            const filename = exportable.filename || `atlas-export-${index + 1}.csv`;
            const label = exportable.label || exportable.title || filename.replace('.csv', '');
            const rows = tr.result[dataPath];

            if (Array.isArray(rows) && rows.length > 0) {
              const csv = this.arrayToCsv(rows);
              exports.push({ csv, filename, label, rowCount: rows.length });
            }
          }
        });

        // Also check for explicit CSV tools
        toolResults.forEach((tr, index) => {
          if (!tr || !tr.toolName) return;
          const name = String(tr.toolName).toLowerCase();
          if (name === 'to_csv' || name === 'to-csv' || name === 'csv' || name === 'export_csv' || name === 'export-csv') {
            if (tr.result && typeof tr.result.csv === 'string') {
              const csv = tr.result.csv;
              const filename = tr.result.filename || `atlas-csv-${index + 1}.csv`;
              const label = tr.result.label || filename.replace('.csv', '');
              exports.push({ csv, filename, label });
            }
          }
        });

        return exports.length > 0 ? exports : null;
      } catch (e) {
        // ignore
      }
      return null;
    },
    extractTableFromResponse(response) {
      try {
        const data = response && response.data ? response.data : response;
        const toolResults = data && Array.isArray(data.toolResults) ? data.toolResults : [];

        // Find any tool result with tableable metadata
        const tableableTool = toolResults.find(tr => {
          return tr && tr.result && tr.result.tableable;
        });

        if (!tableableTool) return null;

        const { tableable } = tableableTool.result;
        const dataPath = tableable.path || 'rows';
        const rows = tableableTool.result[dataPath];

        if (!Array.isArray(rows) || rows.length === 0) return null;

        // Build columns from tableable config or infer from data
        let columns = tableable.columns;
        if (!columns || !Array.isArray(columns) || columns.length === 0) {
          // Infer columns from first row
          columns = Object.keys(rows[0]).map(field => ({
            field,
            label: field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
            sortable: true
          }));
        }

        return {
          title: tableable.title || '',
          columns,
          rows,
          sortField: null,
          sortDir: 'asc'
        };
      } catch (e) {
        console.error('Error extracting table:', e);
        return null;
      }
    },
    extractChartFromResponse(response) {
      try {
        const data = response && response.data ? response.data : response;
        const toolResults = data && Array.isArray(data.toolResults) ? data.toolResults : [];

        const chartableTool = toolResults.find(tr => {
          return tr && tr.result && tr.result.chartable;
        });

        if (!chartableTool) return null;

        const { chartable } = chartableTool.result;
        const dataPath = chartable.path || 'rows';
        const rows = chartableTool.result[dataPath];

        if (!Array.isArray(rows) || rows.length === 0) return null;

        const chartType = chartable.type || 'line';
        const xField = chartable.xField;
        const yFields = chartable.yFields || [];
        const title = chartable.title || '';
        const groupBy = chartable.groupBy;

        const chartConfig = this.buildChartConfig(rows, chartType, xField, yFields, title, groupBy);
        return chartConfig;
      } catch (e) {
        console.error('Error extracting chart:', e);
        return null;
      }
    },
    extractDataFreshness(response) {
      try {
        const data = response && response.data ? response.data : response;
        const toolResults = data && Array.isArray(data.toolResults) ? data.toolResults : [];

        // Look for meta.queriedAt or meta.dataAsOf in any tool result
        for (const tr of toolResults) {
          if (tr && tr.result && tr.result.meta) {
            const meta = tr.result.meta;
            const timestamp = meta.queriedAt || meta.dataAsOf;
            if (timestamp) {
              const date = new Date(timestamp);
              if (!isNaN(date.getTime())) {
                return date.toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                });
              }
            }
          }
        }

        // Fallback: use current time as "responded at"
        return null;
      } catch (e) {
        return null;
      }
    },
    buildChartConfig(rows, chartType, xField, yFields, title, groupBy) {
      const apexChartType = chartType === 'area' ? 'area' : chartType;
      const colors = ['#052f49', '#08c1c8', '#f093fb', '#f5576c', '#4facfe', '#00f2fe', '#43e97b', '#38f9d7'];

      if (groupBy && rows.length > 0 && rows[0][groupBy] !== undefined) {
        const groups = {};
        rows.forEach(row => {
          const groupValue = String(row[groupBy] || 'Unknown');
          if (!groups[groupValue]) {
            groups[groupValue] = [];
          }
          groups[groupValue].push(row);
        });

        const series = [];
        const groupNames = Object.keys(groups);

        yFields.forEach((yField, yIndex) => {
          groupNames.forEach((groupName, gIndex) => {
            const groupRows = groups[groupName];
            series.push({
              name: groupNames.length > 1 ? `${groupName} - ${yField}` : yField,
              data: groupRows.map(row => ({
                x: this.formatXValue(row[xField]),
                y: Number(row[yField]) || 0
              }))
            });
          });
        });

        return {
          options: {
            chart: {
              type: apexChartType,
              toolbar: { show: true, tools: { download: true, zoom: true, pan: true, reset: true } },
              zoom: { enabled: true },
              animations: { enabled: true, speed: 500 }
            },
            colors: colors.slice(0, series.length),
            title: { text: title, align: 'left', style: { fontSize: '14px', fontWeight: 600, color: '#374151' } },
            xaxis: { type: this.detectXAxisType(rows[0][xField]), labels: { style: { colors: '#6b7280', fontSize: '11px' } } },
            yaxis: { labels: { style: { colors: '#6b7280', fontSize: '11px' }, formatter: (val) => this.formatNumber(val) } },
            stroke: { curve: 'smooth', width: 2 },
            fill: { type: chartType === 'area' ? 'gradient' : 'solid', gradient: { shadeIntensity: 0.4, opacityFrom: 0.5, opacityTo: 0.1 } },
            dataLabels: { enabled: false },
            legend: { show: series.length > 1, position: 'top', horizontalAlign: 'left' },
            grid: { borderColor: '#e5e7eb', strokeDashArray: 4 },
            tooltip: { shared: true, intersect: false, y: { formatter: (val) => this.formatNumber(val) } }
          },
          series
        };
      } else {
        const categories = rows.map(row => this.formatXValue(row[xField]));
        const series = yFields.map((yField, index) => ({
          name: yField,
          data: rows.map(row => Number(row[yField]) || 0)
        }));

        if (chartType === 'pie') {
          return {
            options: {
              chart: { type: 'pie', toolbar: { show: true } },
              colors: colors.slice(0, rows.length),
              title: { text: title, align: 'left', style: { fontSize: '14px', fontWeight: 600, color: '#374151' } },
              labels: categories,
              legend: { position: 'bottom' },
              dataLabels: { enabled: true, formatter: (val) => `${val.toFixed(1)}%` },
              tooltip: { y: { formatter: (val) => this.formatNumber(val) } }
            },
            series: rows.map(row => Number(row[yFields[0]]) || 0)
          };
        }

        return {
          options: {
            chart: {
              type: apexChartType,
              toolbar: { show: true, tools: { download: true, zoom: true, pan: true, reset: true } },
              zoom: { enabled: true },
              animations: { enabled: true, speed: 500 }
            },
            colors: colors.slice(0, yFields.length),
            title: { text: title, align: 'left', style: { fontSize: '14px', fontWeight: 600, color: '#374151' } },
            xaxis: { categories, type: this.detectXAxisType(rows[0][xField]), labels: { style: { colors: '#6b7280', fontSize: '11px' }, rotate: -45, rotateAlways: categories.length > 10 } },
            yaxis: { labels: { style: { colors: '#6b7280', fontSize: '11px' }, formatter: (val) => this.formatNumber(val) } },
            stroke: { curve: 'smooth', width: 2 },
            fill: { type: chartType === 'area' ? 'gradient' : 'solid', gradient: { shadeIntensity: 0.4, opacityFrom: 0.5, opacityTo: 0.1 } },
            dataLabels: { enabled: false },
            legend: { show: yFields.length > 1, position: 'top', horizontalAlign: 'left' },
            grid: { borderColor: '#e5e7eb', strokeDashArray: 4 },
            tooltip: { shared: true, intersect: false, y: { formatter: (val) => this.formatNumber(val) } }
          },
          series
        };
      }
    },
    formatXValue(value) {
      if (!value) return '';
      const date = new Date(value);
      if (!isNaN(date.getTime()) && String(value).match(/^\d{4}-\d{2}-\d{2}/)) {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
      return String(value);
    },
    detectXAxisType(sampleValue) {
      if (!sampleValue) return 'category';
      const date = new Date(sampleValue);
      if (!isNaN(date.getTime()) && String(sampleValue).match(/^\d{4}-\d{2}-\d{2}/)) {
        return 'category';
      }
      return 'category';
    },
    formatNumber(val) {
      if (val === null || val === undefined) return '';
      if (val >= 1000000) return (val / 1000000).toFixed(1) + 'M';
      if (val >= 1000) return (val / 1000).toFixed(1) + 'K';
      return Number(val).toLocaleString();
    },
    extractSuggestedFollowUps(response) {
      try {
        const data = response && response.data ? response.data : response;
        const toolResults = data && Array.isArray(data.toolResults) ? data.toolResults : [];

        const allFollowUps = [];
        const seenLabels = new Set();
        const seenPrompts = new Set();

        toolResults.forEach(tr => {
          if (tr && tr.result && Array.isArray(tr.result.suggestedFollowUps)) {
            tr.result.suggestedFollowUps.forEach(fu => {
              if (fu && fu.label && fu.prompt) {
                const labelLower = fu.label.toLowerCase().trim();
                const promptLower = fu.prompt.toLowerCase().trim();

                if (!seenLabels.has(labelLower) && !seenPrompts.has(promptLower)) {
                  seenLabels.add(labelLower);
                  seenPrompts.add(promptLower);
                  allFollowUps.push({ label: fu.label, prompt: fu.prompt });
                }
              }
            });
          }
        });

        return allFollowUps.length > 0 ? allFollowUps : null;
      } catch (e) {
        console.error('Error extracting follow-ups:', e);
        return null;
      }
    },
    extractInterpretationFromResponse(response) {
      try {
        const data = response && response.data ? response.data : response;
        const toolResults = data && Array.isArray(data.toolResults) ? data.toolResults : [];

        // Find the first tool result with interpretation metadata
        for (const tr of toolResults) {
          if (tr && tr.result && tr.result.interpretation) {
            const interp = tr.result.interpretation;
            // Validate required fields
            if (interp.severity && interp.summary) {
              return {
                severity: interp.severity, // 'info' | 'warning' | 'critical'
                summary: interp.summary,
                actionRequired: !!interp.actionRequired,
                details: Array.isArray(interp.details) ? interp.details : []
              };
            }
          }
        }
        return null;
      } catch (e) {
        console.error('Error extracting interpretation:', e);
        return null;
      }
    },
    extractWarningsFromResponse(response) {
      try {
        // Warnings are at the root level of the response, not inside toolResults
        const data = response && response.data ? response.data : response;
        if (data && Array.isArray(data.warnings) && data.warnings.length > 0) {
          return data.warnings.filter(w => typeof w === 'string' && w.trim().length > 0);
        }
        return null;
      } catch (e) {
        console.error('Error extracting warnings:', e);
        return null;
      }
    },
    extractDiagnosticFromResponse(response) {
      try {
        const data = response && response.data ? response.data : response;
        const toolResults = data && Array.isArray(data.toolResults) ? data.toolResults : [];

        // Find diagnostic tool result by tool name
        const diagnosticTool = toolResults.find(tr => {
          if (!tr || !tr.toolName) return false;
          const name = String(tr.toolName).toLowerCase().replace(/[-_]/g, '');
          return name === 'diagnosecampaignforrematch';
        });

        if (!diagnosticTool || !diagnosticTool.result) return null;

        const result = diagnosticTool.result;

        // Validate required fields
        if (!result.recommendation || !result.checks || !result.confidence) return null;

        return {
          campaignId: result.campaignId,
          recommendation: result.recommendation,
          confidence: result.confidence,
          actions: result.actions || [],
          campaignDates: result.campaignDates || {},
          checks: {
            flightDates: result.checks.flightDates || { status: 'ok', flightCount: 0, issues: [] },
            marketAlignment: result.checks.marketAlignment || { status: 'ok', mismatches: [] },
            historicalCampaign: result.checks.historicalCampaign || { isHistorical: false },
            deviceCoverage: result.checks.deviceCoverage || { status: 'ok', inventoryNoDataList: [], inventoryGapsList: [] },
            rematchPrerequisites: result.checks.rematchPrerequisites || { status: 'ok', issues: [], readyForRematch: [] }
          },
          meta: result._meta || null,
          // queries can be at root level or inside _meta
          queries: result.queries || result._meta?.queries || []
        };
      } catch (e) {
        console.error('Error extracting diagnostic:', e);
        return null;
      }
    },
    getDiagnosticVerdictClass(recommendation) {
      const classes = {
        'ALL_GOOD': 'verdict-success',
        'REFRESH_NEEDED': 'verdict-warning',
        'REMATCH_NEEDED': 'verdict-warning',
        'INVESTIGATE_DATA': 'verdict-error',
        'FIX_CONFIGURATION': 'verdict-error'
      };
      return classes[recommendation] || 'verdict-info';
    },
    formatDiagnosticVerdict(recommendation) {
      const labels = {
        'ALL_GOOD': 'All Good',
        'REFRESH_NEEDED': 'Refresh Needed',
        'REMATCH_NEEDED': 'Rematch Needed',
        'INVESTIGATE_DATA': 'Investigate Data',
        'FIX_CONFIGURATION': 'Fix Configuration'
      };
      return labels[recommendation] || recommendation;
    },
    getDiagnosticVerdictIcon(recommendation) {
      // Returns icon type: 'check', 'warning', 'error'
      if (recommendation === 'ALL_GOOD') return 'check';
      if (['REFRESH_NEEDED', 'REMATCH_NEEDED'].includes(recommendation)) return 'warning';
      return 'error';
    },
    getCheckStatusClass(status) {
      const classes = {
        'ok': 'check-ok',
        'warning': 'check-warning',
        'error': 'check-error',
        'critical': 'check-error'
      };
      return classes[status] || 'check-ok';
    },
    toggleDiagnosticCheck(messageIndex, checkName) {
      const current = this.expandedDiagnosticChecks[messageIndex] || {};
      this.expandedDiagnosticChecks = {
        ...this.expandedDiagnosticChecks,
        [messageIndex]: {
          ...current,
          [checkName]: !current[checkName]
        }
      };
    },
    isDiagnosticCheckExpanded(messageIndex, checkName) {
      return !!(this.expandedDiagnosticChecks[messageIndex] && this.expandedDiagnosticChecks[messageIndex][checkName]);
    },
    formatDiagnosticDate(dateStr) {
      if (!dateStr) return '-';
      try {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return dateStr;
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      } catch (e) {
        return dateStr;
      }
    },
    // Dip Diagnostic Tool Methods
    extractDipDiagnosticFromResponse(response) {
      try {
        const data = response && response.data ? response.data : response;
        const toolResults = data && Array.isArray(data.toolResults) ? data.toolResults : [];

        const dipTool = toolResults.find(tr => {
          if (!tr || !tr.toolName) return false;
          const name = String(tr.toolName).toLowerCase().replace(/[-_]/g, '');
          return name === 'diagnosecampaigndip';
        });

        if (!dipTool || !dipTool.result) return null;

        const result = dipTool.result;
        if (!result.recommendation || !result.confidence) return null;

        return {
          campaignId: result.campaignId,
          campaignRef: result.campaignRef,
          recommendation: result.recommendation,
          confidence: result.confidence,
          actions: result.actions || [],
          analysisWindow: result.analysisWindow || {},
          campaignDates: result.campaignDates || {},
          anomalies: result.anomalies || [],
          findings: {
            timing: result.findings?.timing || [],
            dayOfWeek: result.findings?.dayOfWeek || [],
            inventory: result.findings?.inventory || { findings: [] },
            markets: result.findings?.markets || { breakdown: [], significantDrops: [] }
          },
          meta: result._meta || null,
          // queries can be at root level or inside _meta
          queries: result.queries || result._meta?.queries || []
        };
      } catch (e) {
        console.error('Error extracting dip diagnostic:', e);
        return null;
      }
    },
    extractAllQueriesFromResponse(response) {
      try {
        const data = response && response.data ? response.data : response;
        const toolResults = data && Array.isArray(data.toolResults) ? data.toolResults : [];

        const allQueries = [];

        for (const tr of toolResults) {
          if (!tr || !tr.toolName || !tr.result) continue;

          // Check for queries at root level first, then inside _meta
          const queries = tr.result.queries || tr.result._meta?.queries || [];

          if (queries.length > 0) {
            allQueries.push({
              toolName: tr.toolName,
              queries: queries
            });
          }
        }

        return allQueries.length > 0 ? allQueries : null;
      } catch (e) {
        console.error('Error extracting all queries:', e);
        return null;
      }
    },
    getTotalQueryCount(allQueries) {
      if (!allQueries || !Array.isArray(allQueries)) return 0;
      return allQueries.reduce((total, toolQueries) => total + (toolQueries.queries?.length || 0), 0);
    },
    formatToolName(toolName) {
      if (!toolName) return 'Unknown Tool';
      // Convert camelCase or snake_case to Title Case with spaces
      return toolName
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/[_-]/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
    },
    getDipVerdictClass(recommendation) {
      const classes = {
        'NO_ANOMALY': 'dip-success',
        'DATA_LAG': 'dip-info',
        'EXPECTED_PATTERN': 'dip-info',
        'INVENTORY_ISSUE': 'dip-critical',
        'MARKET_ISSUE': 'dip-warning',
        'MULTIPLE_FACTORS': 'dip-critical',
        'INVESTIGATE_FURTHER': 'dip-caution'
      };
      return classes[recommendation] || 'dip-info';
    },
    formatDipVerdict(recommendation) {
      const labels = {
        'NO_ANOMALY': 'No Anomaly',
        'DATA_LAG': 'Data Lag',
        'EXPECTED_PATTERN': 'Expected Pattern',
        'INVENTORY_ISSUE': 'Inventory Issue',
        'MARKET_ISSUE': 'Market Issue',
        'MULTIPLE_FACTORS': 'Multiple Factors',
        'INVESTIGATE_FURTHER': 'Investigate Further'
      };
      return labels[recommendation] || recommendation;
    },
    getDipVerdictIcon(recommendation) {
      if (recommendation === 'NO_ANOMALY') return 'check';
      if (['DATA_LAG', 'EXPECTED_PATTERN'].includes(recommendation)) return 'info';
      if (['INVENTORY_ISSUE', 'MULTIPLE_FACTORS'].includes(recommendation)) return 'error';
      if (recommendation === 'MARKET_ISSUE') return 'warning';
      return 'search';
    },
    getAnomalySeverityClass(severity) {
      const classes = {
        'minor': 'severity-minor',
        'moderate': 'severity-moderate',
        'severe': 'severity-severe'
      };
      return classes[severity] || 'severity-minor';
    },
    formatZScore(zScore) {
      if (zScore === null || zScore === undefined) return '-';
      const num = Number(zScore);
      if (isNaN(num)) return zScore;
      return num.toFixed(2);
    },
    formatChangePercent(value) {
      if (value === null || value === undefined) return '-';
      const num = Number(value);
      if (isNaN(num)) return value;
      const sign = num >= 0 ? '+' : '';
      return `${sign}${num.toFixed(1)}%`;
    },
    async copySqlToClipboard(sql, event) {
      try {
        await navigator.clipboard.writeText(sql);
        // Show visual feedback on the button
        const btn = event.target.closest('.sql-copy-btn');
        if (btn) {
          const originalText = btn.textContent;
          btn.textContent = 'Copied!';
          btn.classList.add('copied');
          setTimeout(() => {
            btn.textContent = originalText;
            btn.classList.remove('copied');
          }, 1500);
        }
      } catch (e) {
        console.error('Failed to copy SQL:', e);
      }
    },
    arrayToCsv(rows) {
      if (!Array.isArray(rows) || rows.length === 0) return '';
      const headers = Object.keys(rows[0]);
      const csvRows = [headers.join(',')];
      for (const row of rows) {
        const values = headers.map(h => {
          const val = row[h];
          if (val === null || val === undefined) return '';
          const str = String(val);
          if (str.includes(',') || str.includes('"') || str.includes('\n')) {
            return '"' + str.replace(/"/g, '""') + '"';
          }
          return str;
        });
        csvRows.push(values.join(','));
      }
      return csvRows.join('\n');
    },
    extractCsvFromText(text) {
      if (!text || typeof text !== 'string') return null;
      const fenceRegex = /```\s*csv\s*\n([\s\S]*?)\n```/i;
      const match = text.match(fenceRegex);
      if (match && match[1]) {
        return { csv: match[1] };
      }
      return null;
    },
    validateCustomButtons() {
      if (!this.customButtons || !Array.isArray(this.customButtons)) {
        console.error('AiChatPanel: customButtons prop must be an array');
        return;
      }

      this.customButtons.forEach((button, index) => {
        if (!button || typeof button !== 'object') {
          console.error(`AiChatPanel: Button at index ${index} is not a valid object`);
          return;
        }

        const requiredProps = ['label', 'systemPrompt', 'userPrompt'];
        const missingProps = requiredProps.filter(prop => !(prop in button));

        if (missingProps.length > 0) {
          console.error(`AiChatPanel: Button at index ${index} is missing required properties: ${missingProps.join(', ')}`);
          return;
        }

        if (!isString(button.label) || button.label.length === 0) {
          console.error(`AiChatPanel: Button at index ${index} has invalid label (must be non-empty string)`);
        }

        if (!isString(button.systemPrompt) || button.systemPrompt.length === 0) {
          console.error(`AiChatPanel: Button at index ${index} has invalid systemPrompt (must be non-empty string)`);
        }

        if (!isString(button.userPrompt) || button.userPrompt.length === 0) {
          console.error(`AiChatPanel: Button at index ${index} has invalid userPrompt (must be non-empty string)`);
        }
      });
    },
    async sendMessage() {
      if (!this.userMessage.trim() || this.isLoading) return;

      const userPrompt = this.userMessage.trim();
      this.userMessage = '';
      this.$nextTick(() => {
        if (this.$refs.messageInput) {
          this.$refs.messageInput.style.height = 'auto';
        }
      });

      this.messages.push({
        role: 'user',
        content: userPrompt
      });

      await this.sendToAI(userPrompt);
    },
    async sendCustomPrompt(button) {
      if (this.isLoading) return;

      this.messages.push({
        role: 'user',
        content: button.userPrompt
      });

      await this.sendToAI(button.userPrompt);
    },
    getRandomPhrase() {
      const index = Math.floor(Math.random() * this.loadingPhrases.length);
      return this.loadingPhrases[index];
    },
    async sendToAI(userPrompt) {
      this.isLoading = true;

      let currentBasePhrase = this.getRandomPhrase();
      const loadingMessage = {
        role: 'assistant',
        content: currentBasePhrase,
        streaming: false,
        isLoadingPhrase: true,
        phraseFading: false
      };
      this.messages.push(loadingMessage);
      const loadingMessageIndex = this.messages.length - 1;
      this.scrollToBottom();

      // Clear any existing intervals
      if (this.loadingPhraseIntervalId) {
        clearInterval(this.loadingPhraseIntervalId);
        this.loadingPhraseIntervalId = null;
      }
      if (this.loadingDotsIntervalId) {
        clearInterval(this.loadingDotsIntervalId);
        this.loadingDotsIntervalId = null;
      }

      // Animate dots every 400ms
      let dotCount = 0;
      this.loadingDotsIntervalId = setInterval(() => {
        const msg = this.messages[loadingMessageIndex];
        if (!msg || !msg.isLoadingPhrase || msg.phraseFading) return;
        dotCount = (dotCount + 1) % 4;
        msg.content = currentBasePhrase + '.'.repeat(dotCount);
      }, 400);

      // Rotate phrases every 5 seconds with fade animation
      this.loadingPhraseIntervalId = setInterval(() => {
        const msg = this.messages[loadingMessageIndex];
        if (!msg || !msg.isLoadingPhrase) return;

        // Start fade out
        msg.phraseFading = true;

        // After fade out, change phrase and fade in
        setTimeout(() => {
          if (this.messages[loadingMessageIndex]?.isLoadingPhrase) {
            currentBasePhrase = this.getRandomPhrase();
            dotCount = 0;
            this.messages[loadingMessageIndex].content = currentBasePhrase;
            this.messages[loadingMessageIndex].phraseFading = false;
          }
        }, 300);
      }, 5000);

      try {
        const response = await this.insightsService.sendAtlasChatMessage(
          userPrompt,
          { includeSnowflake: true },
          this.conversationId,
        );

        if (this.loadingPhraseIntervalId) {
          clearInterval(this.loadingPhraseIntervalId);
          this.loadingPhraseIntervalId = null;
        }
        if (this.loadingDotsIntervalId) {
          clearInterval(this.loadingDotsIntervalId);
          this.loadingDotsIntervalId = null;
        }
        loadingMessage.content = '';
        loadingMessage.streaming = true;

        const fullText = this.extractAssistantText(response) || '';

        let aborted = false;
        this.currentStreamController = { abort: () => { aborted = true; } };

        const words = fullText.split(/(\s+)/);
        let buffer = '';
        const targetChunkSize = 14;
        const delayMs = 140;

        for (let i = 0; i < words.length; i++) {
          if (aborted) break;
          buffer += words[i];
          if (buffer.length >= targetChunkSize || i === words.length - 1) {
            loadingMessage.content += buffer;
            buffer = '';
            this.scrollToBottom();
            await this.$nextTick();
            const lastChar = loadingMessage.content.slice(-1);
            const extraPause = /[\.!?\n]/.test(lastChar) ? 260 : 0;
            await new Promise(resolve => setTimeout(resolve, delayMs + extraPause));
          }
        }
        loadingMessage.streaming = false;

        // Extract CSV if present
        const csvAttachment = this.extractCsvFromResponse(response);
        if (csvAttachment) {
          loadingMessage.csv = csvAttachment;
        }
        if (!loadingMessage.csv) {
          const csvFromText = this.extractCsvFromText(loadingMessage.content);
          if (csvFromText) {
            loadingMessage.csv = csvFromText;
          }
        }

        // Extract table if present
        const tableConfig = this.extractTableFromResponse(response);
        if (tableConfig) {
          loadingMessage.table = tableConfig;
        }

        // Extract chart if present
        const chartConfig = this.extractChartFromResponse(response);
        if (chartConfig) {
          loadingMessage.chart = chartConfig;
        }

        // Extract data freshness if present
        const freshness = this.extractDataFreshness(response);
        if (freshness) {
          loadingMessage.dataFreshness = freshness;
        }

        // Extract suggested follow-ups if present
        const followUps = this.extractSuggestedFollowUps(response);
        if (followUps) {
          loadingMessage.suggestedFollowUps = followUps;
        }

        // Extract interpretation status banner if present
        const interpretation = this.extractInterpretationFromResponse(response);
        if (interpretation) {
          loadingMessage.interpretation = interpretation;
        }

        // Extract warnings from response (token limits, turn counts, etc.)
        const warnings = this.extractWarningsFromResponse(response);
        if (warnings && warnings.length > 0) {
          loadingMessage.warnings = warnings;
        }

        // Extract diagnostic tool result if present
        const diagnostic = this.extractDiagnosticFromResponse(response);
        if (diagnostic) {
          loadingMessage.diagnostic = diagnostic;
        }

        // Extract dip diagnostic tool result if present
        const dipDiagnostic = this.extractDipDiagnosticFromResponse(response);
        if (dipDiagnostic) {
          loadingMessage.dipDiagnostic = dipDiagnostic;
        }

        // Extract all SQL queries from all tools
        const allQueries = this.extractAllQueriesFromResponse(response);
        if (allQueries) {
          loadingMessage.allQueries = allQueries;
        }

        // Attach conversation id for Notion export
        const convId = (response && response.data && response.data.conversationId) || response.conversationId;
        if (convId !== undefined && convId !== null) {
          loadingMessage.conversationId = convId;
          this.conversationId = convId;
        }
      } catch (error) {
        console.error('AI chat error:', error);
        loadingMessage.content = 'Sorry, there was an error processing your request. Please try again.';
      } finally {
        loadingMessage.streaming = false;
        loadingMessage.isLoadingPhrase = false;
        loadingMessage.phraseFading = false;
        if (this.loadingPhraseIntervalId) {
          clearInterval(this.loadingPhraseIntervalId);
          this.loadingPhraseIntervalId = null;
        }
        if (this.loadingDotsIntervalId) {
          clearInterval(this.loadingDotsIntervalId);
          this.loadingDotsIntervalId = null;
        }
        this.currentStreamController = null;
        this.isLoading = false;
        // Scroll to the top of the assistant's response
        this.scrollToMessageTop(loadingMessageIndex);
      }
    },
    downloadCsvExport(csvExport) {
      if (!csvExport || !csvExport.csv) return;
      const filename = csvExport.filename || 'atlas-export.csv';
      this.appUtil.downloadCSV(csvExport.csv, filename, '');
    },
    toggleCsvDropdown(messageIndex) {
      if (this.openCsvDropdownIndex === messageIndex) {
        this.openCsvDropdownIndex = null;
      } else {
        this.openCsvDropdownIndex = messageIndex;
      }
    },
    closeCsvDropdown() {
      this.openCsvDropdownIndex = null;
    },
    toggleToolResults(messageIndex) {
      // Toggle the expanded state for this message's tool results
      this.expandedToolResults = {
        ...this.expandedToolResults,
        [messageIndex]: !this.expandedToolResults[messageIndex]
      };
    },
    isToolResultsExpanded(messageIndex) {
      return !!this.expandedToolResults[messageIndex];
    },
    openNotionModal(message) {
      if (!message || !message.conversationId || message.notionSynced) return;
      this.notionTargetMessage = message;
      this.notionModalMessage = '';
      this.notionModalError = null;
      this.notionModalSending = false;
      this.notionModalVisible = true;
    },
    closeNotionModal() {
      this.notionModalVisible = false;
      this.notionTargetMessage = null;
      this.notionModalMessage = '';
      this.notionModalError = null;
      this.notionModalSending = false;
    },
    async confirmSendToNotion() {
      if (!this.notionTargetMessage || !this.notionTargetMessage.conversationId) return;
      this.notionModalError = null;
      this.notionModalSending = true;
      try {
        await this.insightsService.sendConversationToNotion(
          this.notionTargetMessage.conversationId,
          { note: this.notionModalMessage }
        );
        this.notionTargetMessage.notionSynced = true;
        this.closeNotionModal();
      } catch (e) {
        this.notionModalError = 'Failed to send to Notion';
        this.notionModalSending = false;
      }
    },
    stopStreaming() {
      if (this.currentStreamController) {
        try { this.currentStreamController.abort(); } catch (e) { /* ignore */ }
        this.currentStreamController = null;
        this.isLoading = false;
      }
    },
    resetChat() {
      this.messages = [];
      this.userMessage = '';
      this.conversationId = null;
    },
    toggleSystemPrompt() {
      this.showSystemPrompt = !this.showSystemPrompt;
    },
    scrollToBottom() {
      this.$nextTick(() => {
        if (this.$refs.messagesContainer) {
          this.$refs.messagesContainer.scrollTop = this.$refs.messagesContainer.scrollHeight;
        }
      });
    },
    scrollToMessageTop(messageIndex) {
      this.$nextTick(() => {
        if (this.$refs.messagesContainer) {
          const messageElements = this.$refs.messagesContainer.querySelectorAll('.message');
          const targetMessage = messageElements[messageIndex];
          if (targetMessage) {
            // Smooth scroll to the top of the message with a small offset
            const containerTop = this.$refs.messagesContainer.getBoundingClientRect().top;
            const messageTop = targetMessage.getBoundingClientRect().top;
            const scrollOffset = this.$refs.messagesContainer.scrollTop + (messageTop - containerTop) - 16;

            this.$refs.messagesContainer.scrollTo({
              top: scrollOffset,
              behavior: 'smooth'
            });
          }
        }
      });
    }
  }
};
</script>

<style>
@import '../assets/css/markdown.scss';
</style>

<style scoped>
.ai-chat-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f9fafb;
}

/* Header */
.chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  background: white;
  border-bottom: 1px solid #e5e7eb;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.header-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, #052f49 0%, #08c1c8 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.header-title {
  font-weight: 600;
  font-size: 1rem;
  color: #111827;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
}

.icon-button {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: white;
  color: #6b7280;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.icon-button:hover:not(:disabled) {
  background: linear-gradient(135deg, #e0f7fa 0%, #e0f2fe 100%);
  border-color: #08c1c8;
  color: #052f49;
  transform: scale(1.05);
}

.icon-button:active:not(:disabled) {
  transform: scale(0.95);
}

.icon-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Messages */
.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  padding: 2rem;
}

.empty-icon {
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: linear-gradient(135deg, #052f49 0%, #08c1c8 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin-bottom: 1.5rem;
}

.empty-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.5rem 0;
}

.empty-subtitle {
  color: #6b7280;
  margin: 0;
  font-size: 1rem;
}

.messages-list {
  max-width: 800px;
  margin: 0 auto;
}

/* Message entrance animation */
@keyframes messageSlideIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.message {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  animation: messageSlideIn 0.3s ease-out;
}

/* User message styling */
.message.user {
  flex-direction: row-reverse;
}

.message.user .message-body {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.message.user .message-header-row {
  display: none; /* Hide "You" label - bubble style makes sender obvious */
}

.message.user .message-content {
  background: linear-gradient(135deg, #052f49 0%, #0a4a6e 100%);
  color: white;
  padding: 0.75rem 1rem;
  border-radius: 16px 16px 4px 16px;
  max-width: 85%;
}

.message-avatar {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, #052f49 0%, #08c1c8 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
}

.message-avatar.user-avatar {
  background: linear-gradient(135deg, #052f49 0%, #0a4a6e 100%);
  color: white;
}

.message-body {
  flex: 1;
  min-width: 0;
}

.message-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.25rem;
}

.message-role {
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
}

.copy-button {
  padding: 0.375rem;
  border: none;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.copy-button:hover {
  color: #052f49;
  background: #e0f2fe;
  transform: scale(1.1);
}

.copy-button:active {
  transform: scale(0.95);
}

.message-content {
  color: #374151;
  line-height: 1.6;
  word-wrap: break-word;
}

/* Loading phrase animation */
.message-content.loading-phrase {
  color: #052f49;
  font-style: italic;
  opacity: 1;
  transition: opacity 0.3s ease-in-out;
}

.message-content.loading-phrase.phrase-fading {
  opacity: 0;
}

/* Collapsible Tool Results */
.tool-results-container {
  margin-top: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  background: white;
}

.tool-results-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tool-results-header:hover {
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
}

.tool-results-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.tool-results-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, #052f49 0%, #08c1c8 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
}

.tool-results-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.125rem;
}

.tool-results-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
}

.tool-results-meta {
  font-size: 0.75rem;
  color: #6b7280;
}

.tool-results-toggle {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  color: #6b7280;
  font-size: 0.8125rem;
  font-weight: 500;
}

.toggle-label {
  color: #052f49;
}

.toggle-chevron {
  transition: transform 0.3s ease;
}

.toggle-chevron.expanded {
  transform: rotate(180deg);
}

.tool-results-content {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease-out, opacity 0.2s ease;
  opacity: 0;
}

.tool-results-content.expanded {
  max-height: 800px;
  opacity: 1;
  transition: max-height 0.4s ease-in, opacity 0.3s ease;
}

.tool-results-content .message-table-container {
  margin-top: 0;
  border: none;
  border-radius: 0;
  border-top: 1px solid #e5e7eb;
}

.tool-results-content .message-chart {
  margin-top: 0;
  border: none;
  border-radius: 0;
  border-top: 1px solid #e5e7eb;
}

/* Data Table styles */
.message-table-container {
  margin-top: 1rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
}

.table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.table-title {
  font-weight: 600;
  font-size: 0.875rem;
  color: #374151;
}

.table-count {
  font-size: 0.75rem;
  color: #6b7280;
}

.table-wrapper {
  overflow-x: auto;
  max-height: 400px;
  overflow-y: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;
}

.data-table th {
  position: sticky;
  top: 0;
  background: #f9fafb;
  padding: 0.625rem 0.75rem;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
  user-select: none;
}

.data-table th.sortable {
  cursor: pointer;
}

.data-table th.sortable:hover {
  background: #f3f4f6;
}

.data-table th.sorted {
  color: #052f49;
}

.sort-icon {
  margin-left: 0.25rem;
  display: inline-flex;
  vertical-align: middle;
}

.data-table td {
  padding: 0.625rem 0.75rem;
  border-bottom: 1px solid #f3f4f6;
  color: #374151;
}

.data-table tbody tr:hover {
  background: #f9fafb;
}

.data-table tbody tr:last-child td {
  border-bottom: none;
}

/* Chart styles */
.message-chart {
  margin-top: 1rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1rem;
  overflow: hidden;
}

/* Data freshness indicator */
.data-freshness {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  margin-top: 0.75rem;
  padding: 0.375rem 0.625rem;
  background: #f3f4f6;
  border-radius: 6px;
  font-size: 0.75rem;
  color: #6b7280;
}

/* Interpretation Status Banner */
.interpretation-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  border-left: 4px solid;
}

.interpretation-banner.severity-info {
  background: #eff6ff;
  border-left-color: #3b82f6;
  color: #1e40af;
}

.interpretation-banner.severity-warning {
  background: #fffbeb;
  border-left-color: #f59e0b;
  color: #92400e;
}

.interpretation-banner.severity-critical {
  background: #fef2f2;
  border-left-color: #ef4444;
  color: #991b1b;
}

.banner-icon {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
}

.banner-icon svg {
  width: 16px;
  height: 16px;
}

.severity-info .banner-icon {
  color: #3b82f6;
}

.severity-warning .banner-icon {
  color: #f59e0b;
}

.severity-critical .banner-icon {
  color: #ef4444;
}

.banner-content {
  flex: 1;
  min-width: 0;
}

.banner-summary {
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.35;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.5rem;
}

.action-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.5rem;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  border-radius: 4px;
  background: currentColor;
  color: white;
}

.severity-info .action-badge {
  background: #3b82f6;
}

.severity-warning .action-badge {
  background: #f59e0b;
}

.severity-critical .action-badge {
  background: #ef4444;
}

.banner-details {
  margin: 0.5rem 0 0 0;
  padding-left: 1.125rem;
  font-size: 0.8125rem;
  font-weight: 400;
  line-height: 1.5;
}

.banner-details li {
  margin-bottom: 0.25rem;
}

.banner-details li:last-child {
  margin-bottom: 0;
}

/* Suggested follow-ups */
.suggested-followups {
  margin-top: 1rem;
}

.followups-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.followups-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.followup-button {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #052f49;
  background: linear-gradient(135deg, #e0f7fa 0%, #e0f2fe 100%);
  border: 1px solid #b2ebf2;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.followup-button:hover:not(:disabled) {
  background: linear-gradient(135deg, #b2ebf2 0%, #b3e5fc 100%);
  border-color: #08c1c8;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(8, 193, 200, 0.2);
}

.followup-button:active:not(:disabled) {
  transform: translateY(0);
}

.followup-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Action buttons */
.message-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.action-button {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.875rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #374151;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-button:hover {
  background: #f9fafb;
  border-color: #052f49;
  color: #052f49;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(5, 47, 73, 0.1);
}

.action-button:active {
  transform: translateY(0);
}

.action-button.success {
  background: #ecfdf5;
  border-color: #a7f3d0;
  color: #047857;
}

.action-button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

/* Toolkit Button */
.toolkit-button {
  background: linear-gradient(135deg, #052f49 0%, #0a4a6e 100%);
  color: white;
  border: none;
}

.toolkit-button:hover {
  background: linear-gradient(135deg, #08c1c8 0%, #052f49 100%);
  transform: scale(1.05);
}

/* Recipe Message Display */
.recipe-message {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  padding: 0.875rem 1rem;
  background: linear-gradient(135deg, #f0fdfa 0%, #ecfeff 100%);
  border: 1px solid #99f6e4;
  border-radius: 10px;
}

.recipe-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.6875rem;
  font-weight: 600;
  color: #0d9488;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.recipe-badge svg {
  color: #14b8a6;
}

.recipe-name {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #134e4a;
}

.recipe-params {
  font-size: 0.8125rem;
  color: #5eead4;
  font-family: 'SF Mono', Monaco, 'Cascadia Code', monospace;
  background: rgba(255, 255, 255, 0.5);
  padding: 0.375rem 0.625rem;
  border-radius: 6px;
}

/* CSV Dropdown */
.csv-dropdown-container {
  position: relative;
}

.csv-count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  font-size: 0.6875rem;
  font-weight: 600;
  color: white;
  background: #052f49;
  border-radius: 9px;
  margin-left: 0.25rem;
}

.dropdown-chevron {
  margin-left: 0.25rem;
  transition: transform 0.2s;
}

.dropdown-chevron.open {
  transform: rotate(180deg);
}

.csv-dropdown-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  min-width: 220px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  z-index: 50;
  overflow: hidden;
}

.csv-dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.625rem 0.875rem;
  font-size: 0.8125rem;
  color: #374151;
  background: white;
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;
}

.csv-dropdown-item:hover {
  background: #f3f4f6;
}

.csv-dropdown-item:not(:last-child) {
  border-bottom: 1px solid #f3f4f6;
}

.csv-dropdown-item svg {
  color: #9ca3af;
  flex-shrink: 0;
}

.csv-dropdown-label {
  flex: 1;
  font-weight: 500;
}

.csv-dropdown-count {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 400;
}

.error-text {
  color: #dc2626;
  font-size: 0.75rem;
}

/* System warnings */
.system-warnings {
  margin-top: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.warning-item {
  display: inline-flex;
  align-items: flex-start;
  gap: 0.375rem;
  padding: 0.5rem 0.75rem;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 6px;
  font-size: 0.75rem;
  color: #92400e;
  line-height: 1.4;
}

.warning-item svg {
  flex-shrink: 0;
  margin-top: 0.0625rem;
  color: #f59e0b;
}

/* Input */
.input-section {
  padding: 1rem 1.5rem 1.5rem;
  background: linear-gradient(180deg, #f9fafb 0%, #f3f4f6 100%);
  border-top: 1px solid #e5e7eb;
}

.input-form {
  max-width: 800px;
  margin: 0 auto;
}

.input-wrapper {
  position: relative;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05), inset 0 1px 2px rgba(0, 0, 0, 0.02);
  transition: all 0.2s ease;
}

.input-wrapper:focus-within {
  border-color: #08c1c8;
  box-shadow: 0 4px 20px rgba(5, 47, 73, 0.12), 0 0 0 3px rgba(8, 193, 200, 0.1);
}

.message-input {
  width: 100%;
  padding: 1rem 5.5rem 1rem 1.25rem;
  font-size: 1rem;
  border: none;
  border-radius: 16px;
  background: transparent;
  resize: none;
  outline: none;
  font-family: inherit;
  line-height: 1.5;
  color: #111827;
  min-height: 56px;
  max-height: 200px;
}

.message-input::placeholder {
  color: #9ca3af;
}

.send-button {
  position: absolute;
  right: 8px;
  bottom: 8px;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: none;
  background: #e5e7eb;
  color: #9ca3af;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.send-button.active {
  background: linear-gradient(135deg, #052f49 0%, #08c1c8 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(5, 47, 73, 0.3);
}

.send-button.active:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 16px rgba(5, 47, 73, 0.4);
}

.send-button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.input-footer {
  text-align: center;
  font-size: 0.75rem;
  color: #9ca3af;
  margin-top: 0.75rem;
}

/* Help Button in Input */
.help-button {
  position: absolute;
  right: 52px;
  bottom: 8px;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: none;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}

.help-button:hover {
  background: #f3f4f6;
  color: #6b7280;
}

/* Input Wrapper Container for Tooltip */
.input-wrapper-container {
  position: relative;
}

/* Quick Chips */
.quick-chips-container {
  max-width: 800px;
  margin: 0 auto 0.75rem;
  overflow: hidden;
}

.quick-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
}

.quick-chip {
  padding: 0.5rem 0.875rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #4b5563;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}

.quick-chip:hover {
  border-color: #08c1c8;
  background: #f0fdfa;
  color: #052f49;
}

.quick-chip.more-chip {
  color: #6b7280;
  background: #f9fafb;
}

.quick-chip.more-chip:hover {
  background: #f3f4f6;
  color: #374151;
  border-color: #d1d5db;
}

/* Input Tooltip */
.input-tooltip {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 0.875rem 1rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.12);
  z-index: 100;
  min-width: 280px;
  max-width: 320px;
  animation: tooltipFadeIn 0.2s ease-out;
}

@keyframes tooltipFadeIn {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.input-tooltip::after {
  content: '';
  position: absolute;
  bottom: -6px;
  left: 50%;
  transform: translateX(-50%) rotate(45deg);
  width: 12px;
  height: 12px;
  background: white;
  border-right: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
}

.tooltip-header {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.tooltip-header svg {
  color: #08c1c8;
}

.tooltip-examples {
  list-style: none;
  padding: 0;
  margin: 0 0 0.75rem 0;
}

.tooltip-examples li {
  padding: 0.5rem 0.625rem;
  font-size: 0.8125rem;
  color: #374151;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s;
}

.tooltip-examples li:hover {
  background: #f0fdfa;
  color: #052f49;
}

.tooltip-see-all {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  width: 100%;
  padding: 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
  background: #f9fafb;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;
}

.tooltip-see-all:hover {
  background: #f3f4f6;
  color: #374151;
}

.tooltip-see-all svg {
  color: #9ca3af;
}

/* Spinner */
.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top-color: currentColor;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-container {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.modal-close {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}

.modal-close:hover {
  background: #f3f4f6;
  color: #111827;
}

.modal-body {
  padding: 1.25rem;
}

.modal-description {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0 0 1rem 0;
}

.modal-textarea {
  width: 100%;
  padding: 0.75rem;
  font-size: 0.875rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  resize: vertical;
  outline: none;
  font-family: inherit;
  transition: border-color 0.15s;
}

.modal-textarea:focus {
  border-color: #052f49;
}

.modal-error {
  margin-top: 0.75rem;
  padding: 0.75rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #dc2626;
  font-size: 0.875rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-top: 1px solid #e5e7eb;
}

.modal-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
}

.modal-button.secondary {
  background: white;
  border: 1px solid #e5e7eb;
  color: #374151;
}

.modal-button.secondary:hover {
  background: #f3f4f6;
}

.modal-button.primary {
  background: #052f49;
  border: none;
  color: white;
}

.modal-button.primary:hover {
  opacity: 0.9;
}

.modal-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Campaign Diagnostic Card */
.diagnostic-card {
  margin-top: 1rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
}

.diagnostic-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 1px solid #e5e7eb;
}

.verdict-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 8px;
}

.verdict-badge svg {
  flex-shrink: 0;
}

.verdict-badge.verdict-success {
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  color: #047857;
  border: 1px solid #a7f3d0;
}

.verdict-badge.verdict-warning {
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  color: #b45309;
  border: 1px solid #fcd34d;
}

.verdict-badge.verdict-error {
  background: linear-gradient(135deg, #fef2f2 0%, #fecaca 100%);
  color: #b91c1c;
  border: 1px solid #fca5a5;
}

.confidence-badge {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

.confidence-bar-bg {
  width: 60px;
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
}

.confidence-bar-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s ease;
}

.confidence-badge.high .confidence-bar-fill {
  background: linear-gradient(90deg, #10b981 0%, #059669 100%);
}

.confidence-badge.medium .confidence-bar-fill {
  background: linear-gradient(90deg, #f59e0b 0%, #d97706 100%);
}

.confidence-badge.low .confidence-bar-fill {
  background: linear-gradient(90deg, #ef4444 0%, #dc2626 100%);
}

.confidence-text {
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
  text-transform: capitalize;
}

.diagnostic-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.25rem;
  border-bottom: 1px solid #f3f4f6;
  font-size: 0.8125rem;
}

.campaign-id {
  font-weight: 600;
  color: #052f49;
}

.historical-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.625rem;
  background: #fef3c7;
  color: #92400e;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.date-range {
  color: #6b7280;
}

.diagnostic-section {
  border-bottom: 1px solid #f3f4f6;
}

.diagnostic-section:last-child {
  border-bottom: none;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 1.25rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #374151;
  background: transparent;
  border: none;
  width: 100%;
  text-align: left;
}

.section-header svg {
  color: #6b7280;
  flex-shrink: 0;
}

.section-header.clickable {
  cursor: pointer;
  transition: background 0.15s;
}

.section-header.clickable:hover {
  background: #f9fafb;
}

.section-count {
  margin-left: auto;
  padding: 0.125rem 0.5rem;
  background: #052f49;
  color: white;
  border-radius: 10px;
  font-size: 0.6875rem;
  font-weight: 600;
}

.section-chevron {
  margin-left: auto;
  color: #9ca3af;
  transition: transform 0.2s ease;
}

.section-chevron.expanded {
  transform: rotate(180deg);
}

.actions-list {
  margin: 0;
  padding: 0 1.25rem 1rem 2.75rem;
  list-style: disc;
}

.actions-list li {
  font-size: 0.8125rem;
  color: #374151;
  line-height: 1.6;
  margin-bottom: 0.375rem;
}

.actions-list li:last-child {
  margin-bottom: 0;
}

.checks-list {
  padding: 0 0.75rem 0.75rem;
}

.check-item {
  border-radius: 8px;
  margin-bottom: 0.5rem;
  overflow: hidden;
}

.check-item:last-child {
  margin-bottom: 0;
}

.check-item.check-ok {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
}

.check-item.check-warning {
  background: #fffbeb;
  border: 1px solid #fde68a;
}

.check-item.check-error {
  background: #fef2f2;
  border: 1px solid #fecaca;
}

.check-header {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
  padding: 0.625rem 0.75rem;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;
}

.check-header:hover {
  background: rgba(0, 0, 0, 0.03);
}

.check-status-icon {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.check-ok .check-status-icon {
  color: #16a34a;
}

.check-warning .check-status-icon {
  color: #d97706;
}

.check-error .check-status-icon {
  color: #dc2626;
}

.check-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.check-name {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #1f2937;
}

.check-summary {
  font-size: 0.75rem;
  color: #6b7280;
}

.check-chevron {
  color: #9ca3af;
  transition: transform 0.2s ease;
  flex-shrink: 0;
}

.check-chevron.expanded {
  transform: rotate(180deg);
}

.check-details {
  padding: 0.5rem 0.75rem 0.75rem 2.875rem;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}

.issue-item {
  display: flex;
  gap: 0.5rem;
  font-size: 0.75rem;
  margin-bottom: 0.375rem;
  line-height: 1.4;
}

.issue-item:last-child {
  margin-bottom: 0;
}

.issue-ref {
  font-weight: 600;
  color: #374151;
  flex-shrink: 0;
}

.issue-text {
  color: #6b7280;
}

.implication-text {
  font-size: 0.75rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
}

.coverage-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.75rem;
}

.stat-item {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.stat-label {
  font-size: 0.6875rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.stat-value {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
}

.diagnostic-table-wrapper {
  padding: 0 0.75rem 0.75rem;
  overflow-x: auto;
}

.diagnostic-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.75rem;
}

.diagnostic-table th {
  padding: 0.5rem 0.75rem;
  text-align: left;
  font-weight: 600;
  color: #374151;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
}

.diagnostic-table td {
  padding: 0.5rem 0.75rem;
  color: #374151;
  border-bottom: 1px solid #f3f4f6;
}

.diagnostic-table tbody tr:hover {
  background: #f9fafb;
}

.diagnostic-table tbody tr:last-child td {
  border-bottom: none;
}

.diagnostic-table .ref-cell {
  font-weight: 500;
  color: #052f49;
}

.diagnostic-table .gap-cell {
  color: #dc2626;
  font-weight: 600;
}

.factors-list {
  margin: 0;
  padding: 0 1.25rem 1rem 2.75rem;
  list-style: disc;
}

.factors-list li {
  font-size: 0.75rem;
  color: #6b7280;
  line-height: 1.5;
  margin-bottom: 0.25rem;
}

.factors-list li:last-child {
  margin-bottom: 0;
}

/* Dip Diagnostic Card */
.dip-diagnostic-card {
  margin-top: 1rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
}

.dip-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 1px solid #e5e7eb;
}

.dip-verdict-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 8px;
}

.dip-verdict-badge svg {
  flex-shrink: 0;
}

.dip-verdict-badge.dip-success {
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  color: #047857;
  border: 1px solid #a7f3d0;
}

.dip-verdict-badge.dip-info {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  color: #1d4ed8;
  border: 1px solid #93c5fd;
}

.dip-verdict-badge.dip-warning {
  background: linear-gradient(135deg, #fff7ed 0%, #fed7aa 100%);
  color: #c2410c;
  border: 1px solid #fdba74;
}

.dip-verdict-badge.dip-caution {
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  color: #b45309;
  border: 1px solid #fcd34d;
}

.dip-verdict-badge.dip-critical {
  background: linear-gradient(135deg, #fef2f2 0%, #fecaca 100%);
  color: #b91c1c;
  border: 1px solid #fca5a5;
}

.dip-confidence {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

.dip-info-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1.25rem;
  border-bottom: 1px solid #f3f4f6;
  font-size: 0.8125rem;
}

.dip-campaign-id {
  font-weight: 600;
  color: #052f49;
}

.dip-analysis-window {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  color: #6b7280;
}

.dip-summary {
  padding: 0.875rem 1.25rem;
  font-size: 0.8125rem;
  color: #374151;
  background: #f9fafb;
  border-bottom: 1px solid #f3f4f6;
  line-height: 1.5;
}

.dip-section {
  border-bottom: 1px solid #f3f4f6;
}

.dip-section:last-child {
  border-bottom: none;
}

.dip-section-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.875rem 1.25rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #374151;
  background: transparent;
  border: none;
  text-align: left;
}

.dip-section-header svg {
  color: #6b7280;
  flex-shrink: 0;
}

.dip-section-header.clickable {
  cursor: pointer;
  transition: background 0.15s;
}

.dip-section-header.clickable:hover {
  background: #f9fafb;
}

.dip-section-count {
  margin-left: auto;
  padding: 0.125rem 0.5rem;
  background: #052f49;
  color: white;
  border-radius: 10px;
  font-size: 0.6875rem;
  font-weight: 600;
}

.dip-section-count.dip-count-warning {
  background: #dc2626;
}

.dip-chevron {
  margin-left: auto;
  color: #9ca3af;
  transition: transform 0.2s ease;
}

.dip-chevron.expanded {
  transform: rotate(180deg);
}

.dip-table-wrapper {
  padding: 0 0.75rem 0.75rem;
  overflow-x: auto;
}

.dip-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.75rem;
}

.dip-table th {
  padding: 0.5rem 0.75rem;
  text-align: left;
  font-weight: 600;
  color: #374151;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
}

.dip-table td {
  padding: 0.5rem 0.75rem;
  color: #374151;
  border-bottom: 1px solid #f3f4f6;
}

.dip-table tbody tr:hover {
  background: #f9fafb;
}

.dip-table tbody tr:last-child td {
  border-bottom: none;
}

.dip-table tbody tr.severity-minor {
  background: transparent;
}

.dip-table tbody tr.severity-moderate {
  background: #fffbeb;
}

.dip-table tbody tr.severity-severe {
  background: #fef2f2;
}

.dip-table .zscore-cell {
  font-family: monospace;
  color: #6b7280;
}

.dip-table .market-name-cell {
  font-weight: 500;
}

.dip-table .change-cell {
  font-weight: 600;
}

.dip-table .change-cell.negative {
  color: #dc2626;
}

.dip-table tbody tr.significant-drop {
  background: #fef2f2;
}

.dip-table tbody tr.significant-drop td {
  color: #991b1b;
}

.severity-badge {
  display: inline-block;
  padding: 0.125rem 0.5rem;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  border-radius: 4px;
}

.severity-badge.minor {
  background: #f3f4f6;
  color: #6b7280;
}

.severity-badge.moderate {
  background: #fef3c7;
  color: #92400e;
}

.severity-badge.severe {
  background: #fecaca;
  color: #991b1b;
}

/* Inventory Health Alert */
.dip-inventory-alert {
  margin: 0.75rem;
  padding: 1rem;
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border: 1px solid #fecaca;
  border-radius: 10px;
}

.inventory-alert-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #991b1b;
  margin-bottom: 0.75rem;
}

.inventory-alert-stats {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.inv-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.125rem;
}

.inv-stat-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2937;
}

.inv-stat-label {
  font-size: 0.6875rem;
  color: #6b7280;
  text-transform: uppercase;
}

.inv-stat-arrow {
  font-size: 1.25rem;
  color: #9ca3af;
}

.inv-stat-drop {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.5rem 1rem;
  background: #dc2626;
  border-radius: 8px;
  margin-left: auto;
}

.drop-value {
  font-size: 1rem;
  font-weight: 700;
  color: white;
}

.drop-label {
  font-size: 0.6875rem;
  color: rgba(255, 255, 255, 0.8);
}

/* Findings */
.dip-findings-content {
  padding: 0 1.25rem 1rem;
}

.findings-group {
  margin-bottom: 0.75rem;
}

.findings-group:last-child {
  margin-bottom: 0;
}

.findings-group-title {
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.375rem;
}

.findings-list {
  margin: 0;
  padding-left: 1.25rem;
  list-style: disc;
}

.findings-list li {
  font-size: 0.75rem;
  color: #6b7280;
  line-height: 1.5;
  margin-bottom: 0.25rem;
}

.findings-list li:last-child {
  margin-bottom: 0;
}

/* Dip Actions */
.dip-actions-section {
  background: #f9fafb;
}

.dip-actions-list {
  margin: 0;
  padding: 0 1.25rem 1rem 2.75rem;
  list-style: disc;
}

.dip-actions-list li {
  font-size: 0.8125rem;
  color: #374151;
  line-height: 1.6;
  margin-bottom: 0.375rem;
}

.dip-actions-list li:last-child {
  margin-bottom: 0;
}

.dip-factors-list {
  margin: 0;
  padding: 0 1.25rem 1rem 2.75rem;
  list-style: disc;
}

.dip-factors-list li {
  font-size: 0.75rem;
  color: #6b7280;
  line-height: 1.5;
  margin-bottom: 0.25rem;
}

.dip-factors-list li:last-child {
  margin-bottom: 0;
}

/* SQL Queries Section */
.sql-queries-section {
  background: #f8fafc;
}

.sql-count {
  background: #6b7280 !important;
}

.sql-queries-content {
  padding: 0 1rem 1rem;
}

.sql-query-block {
  margin-bottom: 0.75rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.sql-query-block:last-child {
  margin-bottom: 0;
}

.sql-query-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.sql-query-name {
  font-size: 0.6875rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.sql-copy-btn {
  font-size: 0.6875rem;
  font-weight: 500;
  padding: 0.25rem 0.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background: white;
  color: #374151;
  cursor: pointer;
  transition: all 0.15s;
}

.sql-copy-btn:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
}

.sql-copy-btn.copied {
  background: #ecfdf5;
  border-color: #a7f3d0;
  color: #047857;
}

.sql-code {
  margin: 0;
  padding: 0.75rem;
  background: #1e293b;
  color: #e2e8f0;
  font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 0.6875rem;
  line-height: 1.6;
  overflow-x: auto;
  max-height: 300px;
  white-space: pre;
  tab-size: 2;
}

.sql-code code {
  font-family: inherit;
  color: inherit;
  background: transparent;
}

/* Consolidated SQL Queries Card */
.sql-queries-card {
  margin-top: 1rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
}

.sql-queries-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.875rem 1rem;
  background: #f8fafc;
  border: none;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  transition: background 0.15s;
}

.sql-queries-header:hover {
  background: #f1f5f9;
}

.sql-queries-header svg:first-child {
  color: #6b7280;
  flex-shrink: 0;
}

.sql-queries-header span:first-of-type {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
}

.sql-queries-total-count {
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 0.125rem 0.5rem;
  background: #6b7280;
  color: white;
  border-radius: 9999px;
  margin-left: auto;
}

.sql-queries-chevron {
  color: #9ca3af;
  transition: transform 0.2s;
  flex-shrink: 0;
}

.sql-queries-chevron.expanded {
  transform: rotate(180deg);
}

.sql-queries-body {
  padding: 1rem;
  border-top: 1px solid #e5e7eb;
}

.sql-tool-group {
  margin-bottom: 1rem;
}

.sql-tool-group:last-child {
  margin-bottom: 0;
}

.sql-tool-name {
  font-size: 0.75rem;
  font-weight: 600;
  color: #052f49;
  margin-bottom: 0.5rem;
  padding-bottom: 0.25rem;
  border-bottom: 1px solid #e5e7eb;
}
</style>
